export type Lang = "en" | "ar";

export const content = {
  topbar: {
    location: { en: "Based in Erbil · Serving Iraq", ar: "مقرّنا في أربيل · نخدم العراق" },
    hours: {
      en: "Contact us to arrange support",
      ar: "تواصل معنا لترتيب الخدمة",
    },
    email: { en: "info@iraqcert.com", ar: "info@iraqcert.com" },
  },
  nav: {
    home: { en: "Home", ar: "الرئيسية" },
    about: { en: "About", ar: "عن الشركة" },
    services: { en: "Services", ar: "خدماتنا" },
    industries: { en: "Industries We Serve", ar: "قطاعات العمل" },
    insights: { en: "Insights", ar: "مقالات وأدلة" },
    contact: { en: "Contact", ar: "اتصل بنا" },
    privacy: { en: "Privacy Policy", ar: "سياسة الخصوصية" },
  },
  hero: {
    title: {
      en: "Prepare for ISO. Register as a supplier.",
      ar: "استعدّ للأيزو. سجّل شركتك كمورد.",
    },
    subtitle: {
      en: "We work with your team on management systems, ISO preparation and supplier registration across Iraq.",
      ar: "نعمل مع فريقك على أنظمة الإدارة والتحضير لشهادات الأيزو وتسجيل الموردين في العراق.",
    },
    cta: { en: "Talk to us on WhatsApp", ar: "تواصل معنا عبر واتساب" },
  },
  why: {
    eyebrow: { en: "The IraqCert Advantage", ar: "ميزتنا التنافسية" },
    title: {
      en: "Empowering Your Business to Achieve Operational Excellence",
      ar: "ندعم شركتك في تحسين الأداء التشغيلي",
    },
    points: [
      {
        icon: "Globe",
        title: { en: "National Reach", ar: "حضور وطني شامل" },
        text: {
          en: "Serving clients across all Iraqi provinces with dedicated local and technical expertise.",
          ar: "نقدم خدماتنا لكافة المحافظات العراقية اعتماداً على كفاءات فنية ومحلية متخصصة.",
        },
      },
      {
        icon: "Award",
        title: { en: "Accredited Certification", ar: "شهادات معتمدة" },
        text: {
          en: "Certificates are issued through our partner body AQSR under UAF accreditation (IAF MLA) and can be verified on IAF CertSearch.",
          ar: "تصدر الشهادات عبر شريكنا AQSR باعتماد من UAF (الموقِّعة على اتفاقية الاعتراف الدولي IAF MLA)، ويمكن التحقق منها عبر IAF CertSearch.",
        },
      },
      {
        icon: "Lightbulb",
        title: { en: "Pragmatic Frameworks", ar: "منهجيات عمل تطبيقية" },
        text: {
          en: "We deliver actionable, pragmatic solutions for your most complex operational challenges.",
          ar: "نقدم أطراً استراتيجية قابلة للتنفيذ لمعالجة أعقد التحديات التشغيلية في مؤسستك.",
        },
      },
      {
        icon: "Briefcase",
        title: { en: "Industry Expertise", ar: "خبرة قطاعية متعمقة" },
        text: {
          en: "Over 15 years of specialized experience in quality management and compliance auditing.",
          ar: "أكثر من 15 عاماً من الخبرة المتخصصة في أنظمة إدارة الجودة وتدقيق الامتثال التنظيمي.",
        },
      },
      {
        icon: "Target",
        title: { en: "Tailored Architecture", ar: "أطر عمل مخصصة" },
        text: {
          en: "We customize our compliance frameworks to align precisely with your business scale and sector.",
          ar: "نكيّف أطر الامتثال لتناسب حجم أعمالك وقطاعك.",
        },
      },
      {
        icon: "ShieldCheck",
        title: { en: "End-to-End Support", ar: "دعم فني شامل" },
        text: {
          en: "We support the agreed preparation work, from gap review and implementation to coordination with the proposed certification body.",
          ar: "ندعم أعمال التحضير المتفق عليها، من مراجعة الفجوات والتطبيق إلى التنسيق مع جهة منح الشهادات المقترحة.",
        },
      },
    ],
  },
  iso: {
    eyebrow: { en: "Core ISO Frameworks", ar: "معايير الآيزو الأساسية" },
    subtitle: {
      en: "Internationally recognized standards, with preparation support for Iraq's key industries.",
      ar: "معايير معترف بها دولياً، مع دعم تطبيقها وفق احتياجات القطاعات الحيوية في العراق.",
    },
    readMore: { en: "Read More", ar: "اكتشف المزيد" },
    tabs: [
      {
        label: "ISO 9001",
        title: { en: "Quality Management System", ar: "نظام إدارة الجودة" },
        desc: {
          en: "Establishes a robust Quality Management System focused on meeting regulatory expectations and driving operational performance.",
          ar: "يساعد على بناء نظام متين لإدارة الجودة يركّز على المتطلبات التنظيمية وتحسين الأداء التشغيلي.",
        },
      },
      {
        label: "ISO 14001",
        title: { en: "Environmental Management System", ar: "نظام الإدارة البيئية" },
        desc: {
          en: "Demonstrate environmental stewardship and improve performance through the highly efficient use of operational resources.",
          ar: "أثبت التزام مؤسستك بالمسؤولية البيئية وارتقِ بالأداء عبر الإدارة الفعالة والمستدامة للموارد التشغيلية.",
        },
      },
      {
        label: "ISO 45001",
        title: { en: "Occupational Health & Safety", ar: "الصحة والسلامة المهنية" },
        desc: {
          en: "Provides an institutional framework to optimize safety, mitigate workplace hazards, and prevent occupational injuries.",
          ar: "يوفر إطاراً مؤسسياً لتعزيز السلامة، الحد من مخاطر بيئة العمل، والوقاية من الإصابات المهنية.",
        },
      },
      {
        label: "ISO 22000",
        title: { en: "Food Safety Management", ar: "إدارة سلامة الغذاء" },
        desc: {
          en: "Ensures food safety across the entire supply chain. A crucial standard for Iraqi food producers and distributors.",
          ar: "يضمن تطبيق أعلى معايير سلامة الغذاء عبر سلسلة التوريد بأكملها. معيار حاسم لمنتجي وموزعي الأغذية في العراق.",
        },
      },
      {
        label: "ISO 29001",
        title: { en: "Oil & Gas Quality", ar: "الجودة في قطاع النفط والغاز" },
        desc: {
          en: "Sector-specific quality management requirements for product and service supply organizations within the petroleum industry.",
          ar: "متطلبات إدارة الجودة الصارمة والمصممة خصيصاً لشركات توريد المنتجات والخدمات في قطاعات البترول والغاز.",
        },
      },
    ],
  },
  about: {
    hero: {
      eyebrow: { en: "About IraqCert", ar: "عن شركة عراق سيرت" },
      title: { en: "Our 15-Year Legacy", ar: "خبرة ممتدة لـ 15 عاماً" },
      subtitle: {
        en: "With over 15 years of experience, IraqCert supports companies across Iraq with ISO certification preparation and vendor registration.",
        ar: "بخبرة تتجاوز 15 عاماً، تدعم عراق سيرت الشركات في أنحاء العراق في التحضير لشهادات الأيزو وتسجيل الموردين.",
      },
      cta: { en: "Talk to Our Experts", ar: "تحدث مع خبرائنا" },
    },
    values: {
      eyebrow: { en: "Our Core Values", ar: "قيمنا المؤسسية" },
      title: { en: "Principles That Define Us", ar: "مبادئ راسخة تُعرّف هويتنا" },
      items: [
        {
          title: { en: "Empowerment", ar: "التمكين" },
          text: {
            en: "Enabling Iraqi businesses to reach international markets through certified, world-class quality.",
            ar: "تمكين الشركات العراقية من دخول الأسواق العالمية عبر تطبيق معايير جودة معتمدة دولياً.",
          },
        },
        {
          title: { en: "Integrity", ar: "النزاهة والموضوعية" },
          text: {
            en: "Maintaining strict transparency, objectivity, and confidentiality in every auditing process.",
            ar: "الالتزام التام بالشفافية، الموضوعية، والسرية المهنية في كافة مراحل التدقيق والتقييم.",
          },
        },
        {
          title: { en: "Standardization", ar: "التقييس (أو توحيد المعايير)" },
          text: {
            en: "Aligning local operational capabilities with the rigorous demands of global ISO requirements.",
            ar: "المواءمة الاستراتيجية للإمكانات التشغيلية المحلية مع المتطلبات الصارمة لمعايير الآيزو الدولية.",
          },
        },
      ],
    },
    experience: {
      eyebrow: { en: "Experience in Numbers", ar: "خبرتنا بالأرقام" },
      title: { en: "Supporting companies across Iraq", ar: "ندعم الشركات في أنحاء العراق" },
      stats: [
        { value: "15+", label: { en: "Years of Experience", ar: "عاماً من الخبرة" } },
        { value: "350+", label: { en: "Companies Certified", ar: "شركة حصلت على الشهادة" } },
      ],
    },
    reach: {
      eyebrow: { en: "Serving Iraq", ar: "نطاق التغطية الجغرافية" },
      title: {
        en: "Based in Erbil. Supporting businesses across Iraq.",
        ar: "من أربيل إلى الشركات في أنحاء العراق",
      },
      subtitle: {
        en: "Based in Erbil, we support companies across Iraq remotely and through visits to client premises.",
        ar: "مقرّنا في أربيل، ونخدم الشركات في أنحاء العراق عن بُعد ومن خلال زيارات إلى مواقع العملاء.",
      },
      cities: [
        {
          name: { en: "Erbil", ar: "أربيل" },
          role: { en: "Operating base", ar: "مقرّ العمل" },
          focus: {
            en: "Remote support and agreed client-site visits across Iraq",
            ar: "دعم عن بُعد وزيارات إلى مواقع العملاء في العراق حسب الاتفاق",
          },
        },
      ],
    },
    accreditation: {
      eyebrow: { en: "Accreditation & Recognition", ar: "الاعتماد والاعتراف الدولي" },
      title: {
        en: "Check the Issuing Body and Scope",
        ar: "تحقّق من الجهة المانحة ونطاق الاعتماد",
      },
      body: {
        en: "IraqCert provides preparation support and coordinates certification through partner body AQSR for the disclosed accredited scope. Confirm the proposed standard, issuing body, relevant accreditation scope and buyer acceptance conditions before booking.",
        ar: "تقدّم عراق سيرت دعم التحضير وتنسّق إجراءات منح الشهادات عبر الجهة الشريكة AQSR ضمن نطاق الاعتماد المعلن. تحقّق من المواصفة المقترحة والجهة المانحة ونطاق اعتمادها ذي الصلة وشروط قبول المشتري قبل الحجز.",
      },
      numberLabel: { en: "AQSR's UAF accreditation No.", ar: "رقم اعتماد AQSR لدى UAF" },
      number: "CM-MS-7820",
      scopeLabel: { en: "Accredited scope", ar: "نطاق الاعتماد" },
      scope: ["ISO 9001", "ISO 14001", "ISO 45001", "ISO 27001", "ISO 22000"],
      verify: {
        en: "Verify a certificate on IAF CertSearch",
        ar: "تحقّق من شهادة عبر IAF CertSearch",
      },
      verifyUrl: "https://www.iafcertsearch.org/",
    },
    oilGas: {
      eyebrow: { en: "Energy Sector Specialization", ar: "التخصص في قطاع الطاقة" },
      title: { en: "Specialized ISO 29001 Focus", ar: "تخصص دقيق في معيار الآيزو 29001" },
      text: {
        en: "We understand the unique rigor required for the Iraqi petroleum sector. Our auditors are highly trained in the specific supply chain complexities of oil and gas exploration and production.",
        ar: "ندرك تماماً المعايير الصارمة التي يتطلبها قطاع النفط العراقي. يمتلك مدققونا تأهيلاً عالياً للتعامل مع التعقيدات الدقيقة لسلسلة توريد استكشاف وإنتاج النفط والغاز.",
      },
      points: [
        { en: "Sector-specific quality management", ar: "إدارة جودة مخصصة للقطاع" },
        { en: "Supply chain risk expertise", ar: "خبرة في إدارة مخاطر سلاسل التوريد" },
        { en: "Petroleum industry trained auditors", ar: "مدققون معتمدون في الصناعات البترولية" },
      ],
    },
    process: {
      eyebrow: { en: "Path to Certification", ar: "طريقك نحو الاعتماد" },
      phaseLabel: { en: "Phase", ar: "المرحلة" },
      title: {
        en: "The Strategic Assurance Roadmap",
        ar: "الخارطة الاستراتيجية لضمان الجودة",
      },
      subtitle: {
        en: "A structured, four-phase journey designed for clarity and operational readiness.",
        ar: "رحلة هيكلية متكاملة من أربع مراحل، صُممت لتحقيق الوضوح التام والجاهزية التشغيلية.",
      },
      steps: [
        {
          title: { en: "Diagnostic & Gap Analysis", ar: "التشخيص وتحليل الفجوات" },
          text: {
            en: "We benchmark your current systems against ISO requirements to map the exact path forward.",
            ar: "نقوم بإجراء مقارنة مرجعية لأنظمتك الحالية مقابل متطلبات الآيزو لرسم المسار الدقيق نحو الامتثال.",
          },
        },
        {
          title: {
            en: "Structural Framework & Documentation",
            ar: "الإطار الهيكلي والتوثيق المنهجي",
          },
          text: {
            en: "We build and refine the documented procedures required to meet strict standard demands.",
            ar: "نقوم ببناء وتطوير الإجراءات الموثقة والهيكل التنظيمي الذي تتطلبه المعايير الصارمة.",
          },
        },
        {
          title: { en: "Internal Audit & Validation", ar: "التدقيق الداخلي والتحقق" },
          text: {
            en: "Review implementation and operating records, identify gaps, and agree the work needed before the certification body's assessment.",
            ar: "راجع التطبيق وسجلات العمل، وحدّد الفجوات، واتفق على الأعمال اللازمة قبل تقييم جهة منح الشهادات.",
          },
        },
        {
          title: { en: "Independent Certification Decision", ar: "قرار مستقل بمنح الشهادة" },
          text: {
            en: "The certification body assesses the agreed scope and makes the certification decision. A buyer separately decides whether your evidence meets its requirements.",
            ar: "تقيّم جهة منح الشهادات النطاق المتفق عليه وتتخذ قرار منح الشهادة. ويقرّر المشتري بصورة مستقلة ما إذا كانت أدلتك تستوفي متطلباته.",
          },
        },
      ],
    },
    cta: {
      title: { en: "Your Gateway to Global Excellence", ar: "بوابتك نحو التميز العالمي" },
      subtitle: {
        en: "Partner with IraqCert to turn rigorous standards into a sustainable competitive advantage.",
        ar: "عقد شراكة مع شركة عراق سيرت يحوّل متطلبات الامتثال الصارمة إلى ميزة تنافسية مستدامة.",
      },
      button: { en: "Contact Us", ar: "تواصل معنا" },
    },
  },
  services: {
    hero: {
      eyebrow: { en: "Services Blueprint", ar: "دليل الخدمات الاستراتيجية" },
      title: {
        en: "Our Expertise in Standards",
        ar: "خبرتنا العميقة في المعايير العالمية",
      },
      subtitle: {
        en: "Robust certification frameworks engineered for Iraq's most demanding sectors — from manufacturing to oil & gas.",
        ar: "أطر اعتماد رصينة صُممت لتلبية متطلبات القطاعات الأكثر صرامة في العراق — بدءاً من الصناعات التحويلية وصولاً إلى النفط والغاز.",
      },
      cta: { en: "Request a Consultation", ar: "اطلب استشارة متخصصة" },
    },
    readMore: { en: "Read More", ar: "المزيد من التفاصيل" },
    backToServices: { en: "Back to Services", ar: "العودة إلى الخدمات" },
    whyTitle: { en: "Why It Matters", ar: "الأهمية الاستراتيجية" },
    auditEyebrow: { en: "The Certification Audit", ar: "تدقيق منح الشهادة" },
    auditTitle: {
      en: "Assessment by the Certification Body",
      ar: "التقييم الذي تجريه جهة منح الشهادات",
    },
    auditSubtitle: {
      en: "Confirm the proposed assessment stages and scope with the certification body. Its findings and decision are independent of preparation support.",
      ar: "أكّد مراحل التقييم المقترحة ونطاقه مع جهة منح الشهادات. وتكون نتائجها وقرارها مستقلّين عن دعم التحضير.",
    },
    overview: {
      eyebrow: { en: "Core ISO Services", ar: "خدمات الآيزو الرئيسية" },
      title: { en: "Standards We Certify", ar: "المعايير المعتمدة لدينا" },
      subtitle: {
        en: "Four flagship standards covering quality, environment, safety, and energy.",
        ar: "أربعة معايير ريادية تغطي مجالات الجودة، البيئة، السلامة، والطاقة.",
      },
    },
    isoSection: {
      eyebrow: { en: "Core ISO Standards", ar: "معايير الآيزو الأساسية" },
      title: { en: "Internationally Recognized Standards", ar: "معايير تحظى باعتراف دولي" },
      subtitle: {
        en: "Globally benchmarked certifications covering quality, environment, safety, food, and energy.",
        ar: "شهادات تستند إلى مقاييس عالمية لضمان الامتثال في مجالات الجودة، البيئة، السلامة، الغذاء، والطاقة.",
      },
    },
    energySection: {
      eyebrow: { en: "Energy Sector Registrations", ar: "خدمات قطاع الطاقة" },
      title: { en: "Specialized Oil & Gas Access", ar: "تأهيل متخصص لقطاع النفط والغاز" },
      subtitle: {
        en: "Company dossier and compliance review for buyer-specific oil and gas registration requirements in Iraq.",
        ar: "مراجعة ملفات الشركات ومتطلبات الامتثال للتسجيل لدى الجهات المشترية في قطاع النفط والغاز العراقي.",
      },
    },
    audit: {
      stages: [
        {
          tag: { en: "Stage 1", ar: "المرحلة الأولى" },
          title: { en: "Documentation", ar: "التوثيق والمراجعة" },
          text: {
            en: "The certification body reviews system documentation and readiness, and identifies issues to address before the next assessment stage.",
            ar: "تراجع جهة منح الشهادات وثائق النظام والجاهزية، وتحدّد المسائل المطلوب معالجتها قبل مرحلة التقييم التالية.",
          },
        },
        {
          tag: { en: "Stage 2", ar: "المرحلة الثانية" },
          title: { en: "Implementation", ar: "التطبيق الميداني" },
          text: {
            en: "The certification body assesses implementation and effectiveness through the agreed audit arrangements, interviews and examination of processes and records.",
            ar: "تقيّم جهة منح الشهادات التطبيق والفعالية وفق ترتيبات التدقيق المتفق عليها، ومن خلال المقابلات وفحص العمليات والسجلات.",
          },
        },
      ],
    },
    energyVendors: {
      eyebrow: { en: "Buyer-specific review", ar: "مراجعة بحسب الجهة المشترية" },
      title: { en: "Define the Buyer and Activity First", ar: "حدّد الجهة المشترية والنشاط أولاً" },
      text: {
        en: "There is no single registration that covers every oil company, field and tender. We review an Iraqi company's dossier against the named buyer's current request; that buyer decides eligibility, qualification and approval.",
        ar: "لا يوجد تسجيل واحد يشمل جميع شركات النفط والحقول والمناقصات. نراجع ملف الشركة العراقية وفق الطلب الحالي للجهة المشترية المحددة؛ وتقرر تلك الجهة الأهلية والتأهيل والموافقة.",
      },
      groups: [
        {
          title: { en: "Iraqi State Oil Companies", ar: "شركات النفط الحكومية العراقية" },
          desc: {
            en: "Identify the exact company, procurement contact and requested supply or service category before preparing the file.",
            ar: "حدّد الشركة المعنية وجهة الاتصال بالمشتريات وفئة التوريد أو الخدمة المطلوبة قبل إعداد الملف.",
          },
          tags: [
            "Basra Oil Company (BOC)",
            "North Oil Company (NOC)",
            "Midland Oil Company (MdOC)",
          ],
        },
        {
          title: { en: "Buyer Qualification", ar: "التأهيل لدى الجهة المشترية" },
          desc: {
            en: "Check whether the buyer requests registration, an invitation, prequalification or a category-specific vendor listing. These are separate requirements.",
            ar: "تحقّق مما إذا كانت الجهة تطلب تسجيلاً أو دعوة أو تأهيلاً مسبقاً أو إدراجاً في قائمة موردين لفئة محددة؛ فهذه متطلبات منفصلة.",
          },
          tags: [],
        },
        {
          title: { en: "Tender Requirements", ar: "متطلبات المناقصة" },
          desc: {
            en: "Review the particular tender's conditions separately. A registered profile or approved category does not guarantee an invitation or contract.",
            ar: "راجع شروط المناقصة المعنية بصورة مستقلة؛ فتسجيل الملف أو اعتماد فئة لا يضمن دعوة للمشاركة أو الحصول على عقد.",
          },
          tags: [],
        },
      ],
    },
    biddingAccess: {
      title: { en: "Confirm Each Buyer's Requirements", ar: "تحقّق من متطلبات كل جهة مشترية" },
      text: {
        en: "Certification or registration does not provide automatic tender access. The names below are examples of buyers, not a statement of affiliation or a shared approval route.",
        ar: "لا تمنح الشهادة أو التسجيل حقاً تلقائياً في دخول المناقصات. الأسماء أدناه أمثلة لجهات مشترية، ولا تشير إلى شراكة أو مسار اعتماد مشترك.",
      },
      energyLogos: ["BASRA OIL CO.", "NORTH OIL", "EXXONMOBIL", "BP", "ENI"],
    },
    items: [
      {
        slug: "iso-9001",
        label: "ISO 9001",
        premium: true,
        name: { en: "Quality", ar: "إدارة الجودة" },
        title: {
          en: "ISO 9001 Certification Preparation in Iraq",
          ar: "التحضير لشهادة ISO 9001 في العراق",
        },
        heading: { en: "Mastering Operational Consistency", ar: "تحقيق الاستقرار التشغيلي" },
        card: {
          en: "Operational excellence and customer satisfaction benchmarks for all Iraqi sectors.",
          ar: "إرساء معايير التميز التشغيلي وتحقيق الأداء الأمثل لكافة القطاعات الحيوية في العراق.",
        },
        heroSubtitle: {
          en: "Prepare a quality management system around your activities, customer requirements and the scope you want assessed.",
          ar: "هيّئ نظام إدارة الجودة وفق أنشطة شركتك ومتطلبات عملائك والنطاق الذي تريد تقييمه.",
        },
        text: {
          en: "ISO 9001 sets requirements for a quality management system. Preparation connects your processes, responsibilities and records to the proposed scope. Certification involves assessment of that system; it is not a product approval or a guarantee of contract award.",
          ar: "تحدّد مواصفة ISO 9001 متطلبات نظام إدارة الجودة. يربط التحضير بين عمليات شركتك ومسؤولياتها وسجلاتها والنطاق المقترح. ويُقيَّم هذا النظام في إطار إجراءات منح الشهادة؛ ولا تُعد الشهادة موافقة على المنتج أو ضماناً للفوز بعقد.",
        },
        whyTitle: { en: "The Competitive Edge", ar: "الميزة التنافسية الاستراتيجية" },
        why: [
          {
            lead: {
              en: "Respond to a stated buyer requirement.",
              ar: "الاستجابة لمتطلب محدّد لدى المشتري.",
            },
            body: {
              en: "If a buyer requests ISO 9001, confirm the required scope, issuing body and accreditation conditions before selecting a certification route.",
              ar: "إذا طلب المشتري شهادة ISO 9001، فتحقّق من النطاق المطلوب والجهة المانحة وشروط الاعتماد قبل اختيار مسار الشهادة.",
            },
          },
          {
            lead: {
              en: "Prepare evidence for supplier review.",
              ar: "إعداد الأدلة اللازمة لتقييم المورد.",
            },
            body: {
              en: "Describe the activities to be covered and make relevant operating records available. The buyer decides whether the certificate and other evidence satisfy its supplier requirements.",
              ar: "حدّد الأنشطة المطلوب شمولها وجهّز سجلات العمل ذات الصلة. يقرّر المشتري ما إذا كانت الشهادة والأدلة الأخرى تستوفي متطلباته للموردين.",
            },
          },
        ],
      },
      {
        slug: "iso-14001",
        label: "ISO 14001",
        premium: false,
        name: { en: "Environment", ar: "الإدارة البيئية" },
        title: { en: "Environmental Management", ar: "نظام الإدارة البيئية" },
        heading: { en: "Environmental Stewardship", ar: "المسؤولية والامتثال البيئي" },
        card: {
          en: "Environmental management frameworks to address compliance obligations and reduce operational waste.",
          ar: "أطر للإدارة البيئية تساعد على الوفاء بالتزامات الامتثال وتقليل الهدر التشغيلي.",
        },
        heroSubtitle: {
          en: "An Environmental Management System that optimizes resources and minimizes waste.",
          ar: "نظام إدارة بيئية متطور يهدف إلى تحسين استخدام الموارد وتقليص الهدر إلى الحد الأدنى.",
        },
        text: {
          en: "ISO 14001 helps your company adapt its processes to management systems that protect the environment while reducing operational overheads, turning sustainability into a measurable corporate advantage.",
          ar: "يُمكّن الآيزو 14001 مؤسستك من مواءمة عملياتها التشغيلية مع أنظمة الإدارة البيئية التي تخفض النفقات العامة، مما يُحوّل الالتزام بالاستدامة إلى ميزة تنافسية ملموسة.",
        },
        whyTitle: { en: "Address Environmental Requirements", ar: "الاستجابة للمتطلبات البيئية" },
        why: [
          {
            lead: { en: "Check Buyer Requirements.", ar: "تحقّق من متطلبات المشتري." },
            body: {
              en: "Where a buyer asks for ISO 14001, confirm the required scope and acceptance conditions. Certification does not replace permits or guarantee supplier approval or a contract award.",
              ar: "عندما يطلب المشتري شهادة ISO 14001، تحقّق من النطاق المطلوب وشروط القبول. لا تحل الشهادة محل التصاريح، ولا تضمن اعتماد المورد أو إرساء العقد.",
            },
          },
          {
            lead: { en: "Reduce Operational Overhead.", ar: "خفض النفقات التشغيلية." },
            body: {
              en: "Identify waste and resource use, set improvement objectives, and measure results. Savings depend on the changes your organization implements.",
              ar: "حدّد مصادر الهدر واستهلاك الموارد، وضع أهدافاً للتحسين، وقِس النتائج. يعتمد حجم الوفر على التغييرات التي تطبقها مؤسستك.",
            },
          },
        ],
      },
      {
        slug: "iso-45001",
        label: "ISO 45001",
        premium: false,
        name: { en: "Safety", ar: "السلامة المهنية" },
        title: { en: "Occupational Health & Safety", ar: "نظام الصحة والسلامة المهنية" },
        heading: { en: "Proactive Risk Management", ar: "الإدارة الاستباقية للمخاطر" },
        card: {
          en: "Occupational health frameworks to protect the workforce and minimize operational risk.",
          ar: "أطر مؤسسية للصحة المهنية تهدف إلى حماية الكوادر العاملة وتقليل المخاطر التشغيلية.",
        },
        heroSubtitle: {
          en: "An occupational health and safety management system for identifying hazards and managing workplace risks.",
          ar: "نظام لإدارة الصحة والسلامة المهنية يهدف إلى تحديد الأخطار وإدارة مخاطر بيئة العمل.",
        },
        text: {
          en: "ISO 45001 provides a framework to identify hazards, involve workers, manage occupational health and safety risks, and address applicable obligations. Effective implementation and continued monitoring matter; a certificate alone does not establish legal compliance.",
          ar: "يوفر ISO 45001 إطاراً لتحديد الأخطار وإشراك العاملين وإدارة مخاطر الصحة والسلامة المهنية والوفاء بالالتزامات المنطبقة. ويعتمد ذلك على التطبيق الفعلي والمتابعة المستمرة؛ فالشهادة وحدها لا تثبت الامتثال القانوني.",
        },
        whyTitle: {
          en: "Manage Risk and Buyer Requirements",
          ar: "إدارة المخاطر ومتطلبات المشتري",
        },
        why: [
          {
            lead: { en: "Check Tender Conditions.", ar: "تحقّق من شروط المناقصة." },
            body: {
              en: "A buyer may require ISO 45001 or other health and safety evidence. Check the exact tender or vendor criteria; certification alone does not qualify your company or guarantee an award.",
              ar: "قد يطلب المشتري شهادة ISO 45001 أو أدلة أخرى تتعلق بالصحة والسلامة. راجع معايير المناقصة أو تسجيل الموردين بدقة؛ فالشهادة وحدها لا تؤهل شركتك ولا تضمن إرساء العقد.",
            },
          },
          {
            lead: { en: "Improve Risk Controls.", ar: "تحسين ضوابط المخاطر." },
            body: {
              en: "Use hazard assessments, worker participation and incident reviews to improve controls and monitor safety performance across your operations.",
              ar: "استخدم تقييم الأخطار ومشاركة العاملين ومراجعة الحوادث لتحسين الضوابط ومتابعة أداء السلامة في عملياتك.",
            },
          },
        ],
      },
      {
        slug: "iso-22000",
        label: "ISO 22000",
        premium: false,
        name: { en: "Food Safety", ar: "سلامة الغذاء" },
        title: { en: "ISO 22000: Food Safety", ar: "الآيزو 22000: نظام سلامة الغذاء" },
        heading: {
          en: "Comprehensive Supply Chain Assurance",
          ar: "ضمان الجودة عبر سلسلة التوريد",
        },
        card: {
          en: "Comprehensive frameworks to control food safety hazards and ensure public health.",
          ar: "أطر عمل مؤسسية للسيطرة على مخاطر سلامة الغذاء وضمان حماية الصحة العامة.",
        },
        heroSubtitle: {
          en: "A Food Safety Management System that ensures compliance and unlocks export markets.",
          ar: "نظام متقدم لإدارة سلامة الغذاء يضمن الامتثال التنظيمي ويفتح آفاقاً لأسواق التصدير.",
        },
        text: {
          en: "ISO 22000 integrates HACCP principles with a structured management system to control food safety hazards across the entire supply chain — from raw material sourcing to final distribution.",
          ar: "يدمج معيار الآيزو 22000 مبادئ (HACCP) مع نظام إدارة منهجي ومحكم للسيطرة التامة على مخاطر سلامة الغذاء عبر كافة مراحل سلسلة التوريد — بدءاً من توريد المواد الخام وحتى التوزيع النهائي.",
        },
        whyTitle: {
          en: "Protect Consumers, Expand Markets",
          ar: "ضمان الجودة وتوسيع النطاق التجاري",
        },
        why: [
          {
            lead: { en: "Access Export Markets.", ar: "دخول أسواق التصدير العالمية." },
            body: {
              en: "International buyers and regional regulators demand certified food safety compliance before accepting Iraqi products.",
              ar: "يشترط المشترون الدوليون والجهات التنظيمية الإقليمية وجود شهادات معتمدة لسلامة الغذاء كشرط مسبق لقبول المنتجات العراقية.",
            },
          },
          {
            lead: { en: "Build Institutional Trust.", ar: "بناء الثقة المؤسسية." },
            body: {
              en: "Demonstrate a verifiable commitment to strict hygiene and quality across your production operations.",
              ar: "أظهر التزاماً مؤسسياً موثقاً بأعلى معايير النظافة والجودة عبر كافة عمليات الإنتاج والتوزيع في شركتك.",
            },
          },
        ],
      },
      {
        slug: "iso-29001",
        label: "ISO 29001",
        premium: false,
        name: { en: "Oil & Gas", ar: "قطاع النفط والغاز" },
        title: { en: "The Energy Flagship", ar: "المعيار الرائد لقطاع الطاقة" },
        heading: {
          en: "Petroleum & Gas Industry Rigor",
          ar: "الصرامة التشغيلية في قطاع النفط والغاز",
        },
        card: {
          en: "Sector-specific quality management for petroleum product and service suppliers.",
          ar: "إدارة جودة خاصة بالقطاع لموردي المنتجات والخدمات النفطية.",
        },
        heroSubtitle: {
          en: "Quality-system preparation for the petroleum, petrochemical and natural gas supply chain.",
          ar: "إعداد نظام الجودة لسلسلة توريد النفط والبتروكيماويات والغاز الطبيعي.",
        },
        text: {
          en: "ISO 29001:2020 supplements ISO 9001:2015 with sector requirements and guidance for product and service suppliers in petroleum, petrochemicals and natural gas. Confirm the requested standard and certification scope with the buyer.",
          ar: "تكمل ISO 29001:2020 مواصفة ISO 9001:2015 بمتطلبات وإرشادات قطاعية لموردي المنتجات والخدمات في النفط والبتروكيماويات والغاز الطبيعي. أكّد المواصفة المطلوبة ونطاق الشهادة مع المشتري.",
        },
        whyTitle: {
          en: "Check the intended procurement requirement",
          ar: "راجع متطلب الشراء المستهدف",
        },
        why: [
          {
            lead: { en: "Confirm the operator’s requirement.", ar: "أكّد متطلب الجهة المشغّلة." },
            body: {
              en: "Read the operator’s current tender or supplier-category instructions. ISO 29001 is not a universal condition for every vendor list, and certification does not guarantee registration or a contract.",
              ar: "اقرأ تعليمات المناقصة أو فئة الموردين الحالية لدى المشغّل. ليست ISO 29001 شرطاً عاماً لكل قائمة موردين، ولا تضمن الشهادة التسجيل أو الحصول على عقد.",
            },
          },
          {
            lead: { en: "Prove Supply Chain Rigor.", ar: "إثبات كفاءة سلسلة التوريد." },
            body: {
              en: "Demonstrate that your services meet the strict quality requirements of the petroleum and natural gas industries.",
              ar: "أثبت قدرة خدماتك ومنتجاتك على تلبية متطلبات الجودة الصارمة التي تفرضها صناعات البترول والغاز الطبيعي.",
            },
          },
        ],
      },
      {
        slug: "iso-27001",
        label: "ISO 27001",
        premium: false,
        name: { en: "Security", ar: "الأمن" },
        title: { en: "Information Security", ar: "أمن المعلومات" },
        heading: { en: "Securing Digital Trust", ar: "ترسيخ الثقة الرقمية" },
        card: {
          en: "Prepare an information-security management system with risk assessment, controls and evidence relevant to your activity.",
          ar: "جهّز نظام إدارة أمن المعلومات بتقييم المخاطر والضوابط والأدلة المرتبطة بنشاط شركتك.",
        },
        heroSubtitle: {
          en: "Prepare an ISO 27001 information-security management system. Confirm the issuing body, scope and buyer requirements before arranging certification.",
          ar: "جهّز نظام إدارة أمن المعلومات ISO 27001. تحقق من الجهة المانحة والنطاق ومتطلبات المشتري قبل ترتيب منح الشهادة.",
        },
        text: {
          en: "ISO 27001 sets requirements for an information-security management system. Preparation connects risk assessment, appropriate controls and supporting records to your activities. Certification assessment examines the management system within its scope; it does not guarantee that data is secure or that breaches cannot occur.",
          ar: "يحدد ISO 27001 متطلبات نظام إدارة أمن المعلومات. يربط التحضير تقييم المخاطر والضوابط المناسبة والسجلات الداعمة بأنشطتك. يفحص تقييم منح الشهادة نظام الإدارة ضمن نطاقه؛ ولا يضمن أمن البيانات أو عدم وقوع اختراقات.",
        },
        whyTitle: { en: "The Trust Advantage", ar: "ميزة الثقة" },
        why: [
          {
            lead: { en: "Review buyer requirements.", ar: "راجعوا متطلبات المشتري." },
            body: {
              en: "Where a buyer requests ISO 27001, confirm the required certificate scope and acceptance conditions. Certification does not automatically qualify a supplier, secure a shortlist place or guarantee a contract.",
              ar: "عندما يطلب المشتري ISO 27001، تحقّق من نطاق الشهادة المطلوب وشروط قبولها. لا تمنح الشهادة تأهيلاً تلقائياً للمورد، ولا تضمن إدراجه في قائمة مختصرة أو حصوله على عقد.",
            },
          },
          {
            lead: { en: "Prepare relevant evidence.", ar: "جهّزوا الأدلة ذات الصلة." },
            body: {
              en: "A certificate may form part of a buyer’s security evidence. Buyers can still request questionnaires, technical controls and other assurance. Review their actual requirements instead of treating certification as a replacement for due diligence.",
              ar: "قد تكون الشهادة جزءاً من أدلة الأمن التي يطلبها المشتري. ويمكنه طلب استبيانات وضوابط تقنية وأدلة تحقق إضافية. راجع متطلباته الفعلية بدلاً من اعتبار الشهادة بديلاً عن التحقق اللازم.",
            },
          },
        ],
      },
      {
        slug: "ministry-of-oil-vendor-registration",
        label: "Iraqi Ministry of Oil",
        premium: true,
        name: { en: "Oil & Gas", ar: "قطاع النفط والغاز" },
        title: {
          en: "Iraqi Ministry of Oil Vendor Registration Support",
          ar: "دعم تسجيل الموردين لدى وزارة النفط العراقية",
        },
        heading: {
          en: "Review the Dossier for the Named Oil Company",
          ar: "راجع الملف وفق متطلبات شركة النفط المعنية",
        },
        card: {
          en: "Review your Iraqi company dossier against the registration or qualification request of the named oil company.",
          ar: "راجع ملف شركتك العراقية وفق طلب التسجيل أو التأهيل الصادر عن شركة النفط المعنية.",
        },
        heroSubtitle: {
          en: "Dossier and compliance review for Iraqi companies preparing a buyer-specific Ministry of Oil or state oil company registration request.",
          ar: "مراجعة ملفات الشركات العراقية ومتطلبات الامتثال لطلبات التسجيل المحددة لدى وزارة النفط أو شركات النفط الحكومية.",
        },
        text: {
          en: "Start with the exact buyer, business activity and current registration or prequalification request. Ministry of Oil vendor registration is not one approval covering every state company, field or tender. IraqCert reviews Iraqi company profiles and the legal, financial, technical and HSE evidence requested for that route, identifies gaps and helps organize a consistent dossier. The receiving entity retains its eligibility, qualification and approval decisions.",
          ar: "ابدأ بتحديد الجهة المشترية والنشاط التجاري وطلب التسجيل أو التأهيل المسبق الحالي. لا يعني التسجيل لدى وزارة النفط موافقة واحدة تشمل جميع الشركات الحكومية والحقول والمناقصات. تراجع عراق سيرت الملف التعريفي للشركة العراقية والأدلة القانونية والمالية والفنية ووثائق الصحة والسلامة والبيئة المطلوبة للمسار المعني، وتحدّد النواقص وتساعد في تنظيم ملف متسق. وتبقى قرارات الأهلية والتأهيل والموافقة بيد الجهة المستلمة.",
        },
        whyTitle: { en: "What the Review Covers", ar: "ما تشمله المراجعة" },
        why: [
          {
            lead: { en: "Confirm the Request.", ar: "تأكيد الطلب." },
            body: {
              en: "Match the named entity, requested supply or service category and current instructions. Do not assume a registration for one company applies to another.",
              ar: "طابق الجهة المحددة وفئة التوريد أو الخدمة المطلوبة والتعليمات الحالية. لا تفترض أن التسجيل لدى شركة يسري لدى شركة أخرى.",
            },
          },
          {
            lead: { en: "Review Relevant Evidence.", ar: "مراجعة الأدلة ذات الصلة." },
            body: {
              en: "Check requested legal records, company profile, financial capacity, experience and HSE evidence for consistency and gaps. Insurance and ISO certificates are included when the buyer or activity requires them.",
              ar: "راجع السجلات القانونية المطلوبة والملف التعريفي للشركة والقدرة المالية والخبرة ووثائق الصحة والسلامة والبيئة للتحقّق من اتساقها ونواقصها. تُدرج وثائق التأمين وشهادات الأيزو عندما تشترطها الجهة المشترية أو طبيعة النشاط.",
            },
          },
          {
            lead: { en: "Keep Decisions Separate.", ar: "فصل مراحل القرار." },
            body: {
              en: "Profile registration, prequalification, vendor listing and tender award are distinct outcomes. Dossier support does not guarantee any of them or a fixed approval date.",
              ar: "تسجيل الملف والتأهيل المسبق والإدراج في قائمة الموردين وإرساء المناقصة نتائج منفصلة. لا يضمن دعم إعداد الملف أياً منها أو موعداً ثابتاً للموافقة.",
            },
          },
        ],
      },
      {
        slug: "oil-gas-registration",
        label: "Oil & Gas",
        premium: true,
        name: { en: "Oil & Gas", ar: "قطاع النفط والغاز" },
        title: {
          en: "Oil & Gas Vendor Registration in Iraq",
          ar: "تسجيل موردي النفط والغاز في العراق",
        },
        heading: {
          en: "Prepare a Buyer-specific Company Dossier",
          ar: "جهّز ملف الشركة وفق متطلبات الجهة المشترية",
        },
        card: {
          en: "Dossier and compliance review for Iraqi companies preparing oil and gas supplier registration or qualification files.",
          ar: "مراجعة ملفات الشركات العراقية ومتطلبات الامتثال لإعداد طلبات تسجيل موردي النفط والغاز أو تأهيلهم.",
        },
        heroSubtitle: {
          en: "Review your company profile and requested evidence for the right buyer, activity and registration route in Iraq or the Kurdistan Region.",
          ar: "راجع تعريف شركتك والأدلة المطلوبة وفق الجهة المشترية والنشاط ومسار التسجيل المناسب في العراق أو إقليم كردستان.",
        },
        text: {
          en: "Oil and gas vendor registration depends on the buyer and the products or services you supply. IraqCert helps Iraqi companies review and organize company profiles, requested compliance documents and supporting evidence. Start with the named buyer or project and its current instructions; registration, category approval and tender qualification may be separate steps. No profile, certificate or completed dossier guarantees vendor approval, an invitation or a contract.",
          ar: "يعتمد تسجيل موردي النفط والغاز على الجهة المشترية والمنتجات أو الخدمات التي تقدمها الشركة. تساعد عراق سيرت الشركات العراقية على مراجعة الملف التعريفي للشركة ووثائق الامتثال المطلوبة والأدلة الداعمة وتنظيمها. ابدأ بالجهة المشترية أو المشروع المحدد وتعليماته الحالية؛ فقد يكون التسجيل واعتماد الفئة والتأهيل للمناقصة مراحل منفصلة. لا يضمن أي ملف أو شهادة أو اكتمال للوثائق اعتماد المورد أو توجيه دعوة أو الحصول على عقد.",
        },
        whyTitle: { en: "What We Review", ar: "ما الذي نراجعه" },
        why: [
          {
            lead: { en: "Buyer and Activity.", ar: "الجهة المشترية والنشاط." },
            body: {
              en: "Identify the relevant registration or qualification request for BOC, Rumaila, BGC or MNR. Their routes and eligibility conditions differ; one registration does not cover every buyer.",
              ar: "حدّد طلب التسجيل أو التأهيل المعني لدى شركة نفط البصرة أو الرميلة أو شركة غاز البصرة أو وزارة الثروات الطبيعية. تختلف المسارات وشروط الأهلية بينها؛ ولا يشمل تسجيل واحد جميع الجهات المشترية.",
            },
          },
          {
            lead: { en: "Company Profile and Evidence.", ar: "الملف التعريفي للشركة والأدلة." },
            body: {
              en: "Align the profile and scope with current requested legal, financial, technical and HSE evidence. Flag missing, expired or inconsistent documents before the agreed next step.",
              ar: "طابق الملف التعريفي للشركة ونطاق نشاطها مع الأدلة القانونية والمالية والفنية ووثائق الصحة والسلامة والبيئة المطلوبة حالياً. حدّد الوثائق الناقصة أو المنتهية أو غير المتسقة قبل الخطوة التالية المتفق عليها.",
            },
          },
          {
            lead: { en: "Conditional Certificates.", ar: "الشهادات بحسب المتطلبات." },
            body: {
              en: "Check the actual buyer and activity criteria before commissioning ISO certification. There is no universal ISO requirement covering every oil and gas vendor route.",
              ar: "تحقّق من المعايير الفعلية للجهة المشترية والنشاط قبل التعاقد للحصول على شهادة أيزو. لا يوجد شرط أيزو موحد يشمل جميع مسارات تسجيل موردي النفط والغاز.",
            },
          },
          {
            lead: { en: "A Defined Review Scope.", ar: "نطاق مراجعة محدد." },
            body: {
              en: "Agree which dossier and compliance gaps we will review. Any submission support is scoped separately; the buyer controls approval and its review timetable.",
              ar: "اتّفق معنا على الملف ونواقص الامتثال المشمولة بالمراجعة. يُحدّد أي دعم للتقديم بنطاق منفصل؛ وتتحكم الجهة المشترية في الموافقة وجدول مراجعتها.",
            },
          },
        ],
      },
    ],
    cta: {
      title: { en: "Start with a defined scope.", ar: "ابدأ بنطاق عمل محدّد." },
      subtitle: {
        en: "Share your business activity, the buyer’s requirement and your target date so we can scope the work.",
        ar: "اذكر نشاط شركتك ومتطلب المشتري والموعد المستهدف لنحدّد نطاق العمل.",
      },
      button: { en: "Contact Our Team", ar: "تواصل مع فريق الخبراء" },
    },
  },
  industries: {
    hero: {
      eyebrow: { en: "Industries We Serve", ar: "قطاعات العمل" },
      title: {
        en: "Built for Iraq's Most Demanding Sectors.",
        ar: "معايير تلبي تطلعات القطاعات الأكثر صرامة في العراق.",
      },
      subtitle: {
        en: "From the oil fields of Basra to national infrastructure projects, we engineer certification strategies that prequalify you for major contracts.",
        ar: "من حقول النفط في البصرة إلى مشاريع البنية التحتية الوطنية، نؤسس استراتيجيات اعتماد تؤهلك للمنافسة بقوة على العقود الكبرى.",
      },
    },
    cardsEyebrow: { en: "Sector Expertise", ar: "الخبرات القطاعية" },
    cardsTitle: { en: "Where We Create Impact", ar: "أين نصنع الأثر الحقيقي" },
    items: [
      {
        icon: "droplet",
        title: { en: "Oil & Gas", ar: "قطاع النفط والغاز" },
        text: {
          en: "Securing supply chains and ensuring strict compliance for upstream and downstream operations.",
          ar: "تأمين سلاسل التوريد وضمان الامتثال التنظيمي الصارم لكافة عمليات التنقيب والإنتاج والتكرير.",
        },
      },
      {
        icon: "hardhat",
        title: { en: "Construction & Infrastructure", ar: "الإنشاءات والبنية التحتية" },
        text: {
          en: "Qualifying your firm for mega-projects through rigorous occupational safety and quality management.",
          ar: "تأهيل مؤسستك للمنافسة على المشاريع العملاقة عبر تطبيق أحدث أنظمة إدارة الجودة والسلامة المهنية.",
        },
      },
      {
        icon: "landmark",
        title: { en: "Government & Public Sector", ar: "الحكومة والقطاع العام" },
        text: {
          en: "Meeting the strict regulatory frameworks required to secure national and provincial government tenders.",
          ar: "تلبية كافة الأطر التنظيمية والقانونية الصارمة المشترطة للتأهل للعطاءات والمناقصات الحكومية.",
        },
      },
      {
        icon: "settings",
        title: { en: "Manufacturing", ar: "الصناعات التحويلية" },
        text: {
          en: "Driving operational efficiency, reducing waste, and standardizing quality across production lines.",
          ar: "تعزيز الكفاءة التشغيلية، الحد من الهدر الصناعي، وتوحيد معايير الجودة عبر كافة خطوط الإنتاج.",
        },
      },
      {
        icon: "wheat",
        title: { en: "Food & Beverage", ar: "قطاع الأغذية والمشروبات" },
        text: {
          en: "Ensuring safety and quality from production to distribution, protecting public health and opening export markets.",
          ar: "ضمان سلامة وجودة المنتجات من خطوط الإنتاج وحتى التوزيع، حمايةً للصحة العامة وتمهيداً لاختراق أسواق التصدير.",
        },
      },
    ],
    cta: {
      title: {
        en: "Ready to Qualify for Your Next Bid?",
        ar: "هل أنت مستعد للتأهل للمناقصات الاستراتيجية القادمة؟",
      },
      subtitle: {
        en: "Let our experts map the exact standards your sector requires.",
        ar: "دع خبراءنا يحددون لك المعايير الدقيقة المطلوبة للريادة في قطاعك.",
      },
      button: { en: "Start Your Certification", ar: "ابدأ عملية التأهيل" },
    },
  },
  contact: {
    title: { en: "Get in Touch", ar: "تواصل معنا" },
    subtitle: {
      en: "Start your certification journey today.",
      ar: "ابدأ رحلتك نحو الاعتماد المؤسسي اليوم.",
    },
    locationsLabel: { en: "Our base and coverage", ar: "مقرّنا ونطاق خدماتنا" },
    officesTitle: { en: "Our base and coverage", ar: "مقرّنا ونطاق خدماتنا" },
    offices: [
      {
        id: "erbil",
        isHQ: true,
        city: { en: "Based in Erbil", ar: "مقرّنا في أربيل" },
        address: { en: "Serving companies across Iraq", ar: "نخدم الشركات في أنحاء العراق" },
        mapSubtitle: {
          en: "Contact us by phone or message to arrange support remotely or a visit to your premises.",
          ar: "تواصل معنا هاتفياً أو برسالة لترتيب الخدمة عن بُعد أو زيارة موقع شركتك.",
        },
      },
    ],
    phoneLabel: { en: "Phone", ar: "الهاتف المباشر" },
    emailLabel: { en: "Email", ar: "البريد الإلكتروني" },
    formTitle: { en: "Request a Consultation", ar: "طلب استشارة مهنية" },
    fields: {
      name: { en: "Full Name", ar: "الاسم الكامل" },
      company: { en: "Company Name", ar: "اسم المؤسسة / الشركة" },
      email: { en: "Email", ar: "البريد الإلكتروني للعمل" },
      phone: { en: "Phone", ar: "رقم الهاتف" },
      message: { en: "Message", ar: "تفاصيل الطلب أو الاستفسار" },
    },
    submit: { en: "Request Consultation", ar: "إرسال طلب الاستشارة" },
    success: {
      en: "Thank you. Our team will contact you shortly.",
      ar: "شكراً لتواصلكم مع شركة عراق سيرت. سيقوم فريقنا بالاتصال بكم قريباً.",
    },
    errors: {
      name: { en: "Please enter your full name.", ar: "يرجى إدخال الاسم الكامل بشكل صحيح." },
      email: {
        en: "Please enter a valid email address.",
        ar: "يرجى إدخال عنوان بريد إلكتروني صالح.",
      },
      message: { en: "Please enter a message.", ar: "يرجى إدخال تفاصيل رسالتك أو استفسارك." },
    },
  },
  faq: {
    eyebrow: { en: "Answers", ar: "إجابات" },
    title: { en: "Frequently Asked Questions", ar: "الأسئلة الشائعة" },
    // Arabic drafted in MSA — flag for native review before final go-live.
    home: [
      {
        q: { en: "What is ISO certification?", ar: "ما هي شهادة الأيزو؟" },
        a: {
          en: "ISO certification is independent, third-party confirmation that your management system meets an international standard. An accredited certification body audits your company and issues the certificate. ISO writes the standards but does not certify companies itself.",
          ar: "شهادة الأيزو هي تأكيد مستقل من طرف ثالث بأن نظام الإدارة في شركتك يفي بمعيار دولي. تقوم جهة مانحة معتمدة بتدقيق شركتك وإصدار الشهادة. أما منظمة الأيزو فتضع المعايير ولا تمنح الشهادات بنفسها.",
        },
      },
      {
        q: { en: "Is ISO certification mandatory?", ar: "هل شهادة الأيزو إلزامية؟" },
        a: {
          en: "ISO certification is voluntary, not required by law. In practice it is often required to qualify for government tenders, supplier registration, and international contracts, so most companies treat it as a commercial necessity.",
          ar: "شهادة الأيزو اختيارية وليست مفروضة بالقانون. لكنها غالباً ما تكون شرطاً للتأهل للمناقصات الحكومية وتسجيل الموردين والعقود الدولية، لذا تتعامل معها معظم الشركات كضرورة تجارية.",
        },
      },
      {
        q: { en: "How long is an ISO certificate valid?", ar: "كم مدة صلاحية شهادة الأيزو؟" },
        a: {
          en: "An ISO certificate is valid for three years. You pass a surveillance audit each year and a full recertification audit at the end of the cycle to renew it.",
          ar: "شهادة الأيزو صالحة لمدة ثلاث سنوات. تجتاز خلالها تدقيقاً رقابياً كل عام، ثم تدقيقاً كاملاً لتجديد الشهادة في نهاية الدورة.",
        },
      },
      {
        q: {
          en: "How long does it take to get ISO certified?",
          ar: "كم يستغرق الحصول على شهادة الأيزو؟",
        },
        a: {
          en: "Timing depends on your readiness, scope, sites, corrective actions and the issuing body’s availability. Agree an indicative schedule after the requirement is reviewed; no completion date is guaranteed.",
          ar: "تتوقف المدة على جاهزيتك والنطاق والمواقع والإجراءات التصحيحية وتوافر مواعيد لدى جهة المنح. اتفق على جدول زمني تقديري بعد مراجعة المتطلب؛ ولا يوجد ضمان لموعد إتمام محدد.",
        },
      },
      {
        q: {
          en: "What is the difference between accredited and non-accredited certification?",
          ar: "ما الفرق بين الشهادة المعتمدة وغير المعتمدة؟",
        },
        a: {
          en: "Accreditation concerns independent oversight of a certification body for a defined scope. A buyer may require accreditation from a particular body or scheme. Check the issuer, accreditation scope and buyer’s acceptance criteria; do not assume every certificate is accepted.",
          ar: "يتعلق الاعتماد بالرقابة المستقلة على جهة منح الشهادات ضمن نطاق محدد. قد يشترط المشتري اعتماداً من هيئة أو برنامج معين. تحقّق من جهة المنح ونطاق اعتمادها وشروط قبول المشتري؛ ولا تفترض قبول كل شهادة.",
        },
      },
      {
        q: {
          en: "How do I verify an ISO certificate is genuine?",
          ar: "كيف أتحقق من صحة شهادة الأيزو؟",
        },
        a: {
          en: "Search IAF CertSearch by certificate number or company name and compare the standard, scope, status and issuing body with the certificate. A missing result alone does not prove non-accreditation; follow up with the issuer, accreditation body or IAF CertSearch before deciding.",
          ar: "ابحث في IAF CertSearch برقم الشهادة أو اسم الشركة، وطابق المواصفة والنطاق والحالة والجهة المانحة مع بيانات الشهادة. عدم ظهور نتيجة لا يثبت وحده غياب الاعتماد؛ تواصل مع الجهة المانحة أو هيئة الاعتماد أو دعم IAF CertSearch قبل اتخاذ القرار.",
        },
      },
      {
        q: {
          en: "Is IraqCert a trusted source for ISO certification in Iraq?",
          ar: "هل عراق سيرت مصدر موثوق لشهادات الأيزو في العراق؟",
        },
        a: {
          en: "According to company-reported figures, IraqCert has over 15 years of experience and has supported more than 350 companies in obtaining certification. Based in Erbil, it serves companies across Iraq. Confirm the proposed issuing body and accreditation scope before booking. Certificates are issued through partner body AQSR, accredited by the United Accreditation Foundation (UAF), an IAF MLA signatory, and can be verified on IAF CertSearch. ISO 29001 is outside AQSR’s disclosed UAF-accredited scope; confirm the issuing body and its accreditation scope before booking an ISO 29001 audit.",
          ar: "وفقاً للأرقام التي تعلنها الشركة، تتمتع عراق سيرت بخبرة تتجاوز 15 عاماً، ودعمت أكثر من 350 شركة في الحصول على الشهادة. مقرّها في أربيل وتخدم الشركات في أنحاء العراق. تحقّق من جهة المنح المقترحة ونطاق اعتمادها قبل الحجز. تصدر الشهادات عبر جهة المنح الشريكة AQSR المعتمدة من مؤسسة الاعتماد المتحدة (UAF)، وهي هيئة موقعة على اتفاقية IAF MLA، ويمكن التحقق منها على منصة IAF CertSearch. معيار ISO 29001 خارج نطاق اعتماد UAF المعلن لشركة AQSR؛ تحقّق من الجهة المانحة ونطاق اعتمادها قبل حجز تدقيق ISO 29001.",
        },
      },
    ],
    about: [
      {
        q: {
          en: "Is IraqCert a trusted source for ISO certification?",
          ar: "هل عراق سيرت مصدر موثوق لشهادات ايزو؟",
        },
        a: {
          en: "According to company-reported figures, IraqCert has over 15 years of experience and has supported more than 350 companies in obtaining certification. Based in Erbil, it serves companies across Iraq. Confirm the proposed issuing body and accreditation scope before booking. AQSR’s accreditation should be checked against the specific standard and certification scope being proposed.",
          ar: "وفقاً للأرقام التي تعلنها الشركة، تتمتع عراق سيرت بخبرة تتجاوز 15 عاماً، ودعمت أكثر من 350 شركة في الحصول على الشهادة. مقرّها في أربيل وتخدم الشركات في أنحاء العراق. تحقّق من جهة المنح المقترحة ونطاق اعتمادها قبل الحجز. ينبغي التحقّق من اعتماد AQSR وفقاً للمعيار ونطاق الشهادة المقترحين.",
        },
      },
      {
        q: {
          en: "What accreditation stands behind IraqCert certificates?",
          ar: "ما الاعتماد الذي يقف خلف شهادات عراق سيرت؟",
        },
        a: {
          en: "According to company-reported partner information, certificates are issued through partner certification body AQSR (Americo Quality Standards Registech), accredited by the United Accreditation Foundation (UAF), an IAF MLA signatory, under accreditation number CM-MS-7820. The scope covers ISO 9001, ISO 14001, ISO 45001, ISO 27001, and ISO 22000.",
          ar: "وفقاً لمعلومات الشراكة التي تعلنها الشركة، تصدر الشهادات عبر جهة المنح الشريكة AQSR (أميركو كواليتي ستاندردز ريجيستيك) المعتمدة من مؤسسة الاعتماد المتحدة (UAF)، وهي هيئة موقعة على اتفاقية IAF MLA، برقم اعتماد CM-MS-7820. يغطي النطاق ISO 9001 وISO 14001 وISO 45001 وISO 27001 وISO 22000.",
        },
      },
      {
        q: {
          en: "How can I verify a certificate issued through IraqCert?",
          ar: "كيف أتحقق من شهادة صادرة عبر عراق سيرت؟",
        },
        a: {
          en: "Search IAF CertSearch using the company name or certificate number. Compare the standard, scope, status, certification body and accreditation body with your certificate. If the result is missing or unclear, ask the issuing body, accreditation body or IAF CertSearch to confirm the record.",
          ar: "ابحث في IAF CertSearch باسم الشركة أو برقم الشهادة. وطابق المواصفة والنطاق والحالة وجهة المنح وهيئة الاعتماد مع بيانات شهادتك. وإذا لم تظهر نتيجة أو كانت غير واضحة، فاطلب تأكيد السجل من الجهة المانحة أو هيئة الاعتماد أو دعم IAF CertSearch.",
        },
      },
    ],
    servicesHub: [
      {
        q: {
          en: "What are the requirements to get ISO certified in Iraq?",
          ar: "ما هي شروط الحصول على شهادة الأيزو في العراق؟",
        },
        a: {
          en: "Getting ISO certified in Iraq requires a documented management system that matches the standard, evidence that it runs in practice, and a certification audit by an accredited certification body. The path is gap analysis, documentation and implementation, internal audit and management review, then a two-stage certification audit. IraqCert prepares your system and manages the process. The certificate is issued by our partner body AQSR, accredited by UAF, and verifiable on IAF CertSearch.",
          ar: "الحصول على شهادة الأيزو في العراق يتطلب نظام إدارة موثقاً يطابق متطلبات المواصفة، وأدلة على تطبيقه الفعلي، وتدقيقاً خارجياً من جهة مانحة للشهادات معتمدة. يبدأ المسار بتحليل الفجوات، ثم التوثيق والتطبيق، يليهما التدقيق الداخلي ومراجعة الإدارة، وينتهي بتدقيق منح الشهادة على مرحلتين. عراق سيرت يجهز نظامك ويدير العملية كاملة. الشهادة تصدر عن شريكنا AQSR المعتمد من هيئة الاعتماد UAF، ويمكن التحقق منها في منصة IAF CertSearch.",
        },
      },
      {
        q: { en: "What are the types of ISO certificates?", ar: "ما هي أنواع شهادات الأيزو؟" },
        a: {
          en: "The main ISO management system certificates are ISO 9001 for quality management, ISO 14001 for environmental management, ISO 45001 for occupational health and safety, ISO 22000 for food safety, ISO 27001 for information security, and ISO 29001 for quality in the oil and gas supply chain. Each certifies a different management system, and one organization can hold several at once. Iraqi tenders most often ask for ISO 9001, ISO 14001, and ISO 45001 together.",
          ar: "أنواع شهادات الأيزو الرئيسية لأنظمة الإدارة هي: ايزو 9001 لإدارة الجودة، وايزو 14001 للإدارة البيئية، وايزو 45001 للصحة والسلامة المهنية، وايزو 22000 لسلامة الغذاء، وايزو 27001 لأمن المعلومات، وايزو 29001 للجودة في سلسلة توريد النفط والغاز. كل شهادة منها تخص نظام إدارة مختلفاً، ويمكن للمنشأة الواحدة الجمع بين أكثر من شهادة في وقت واحد. المناقصات العراقية تطلب في الغالب ايزو 9001 وايزو 14001 وايزو 45001 معاً.",
        },
      },
      {
        q: {
          en: "Can I get an ISO certificate online in Iraq?",
          ar: "هل يمكن الحصول على شهادة الأيزو أونلاين؟",
        },
        a: {
          en: "Parts of preparation and auditing may be carried out remotely where the certification body permits it for the scope and circumstances. Accredited management-system certification requires an audit and a certification decision; an instant certificate offered without assessment is a warning sign. Verify the issuing body, relevant accreditation scope and the buyer’s acceptance conditions before paying.",
          ar: "يمكن تنفيذ بعض أعمال التحضير والتدقيق عن بُعد إذا سمحت الجهة المانحة بذلك وفق نطاق العمل وظروفه. ويتطلب منح شهادة معتمدة لنظام الإدارة إجراء تدقيق واتخاذ قرار بالمنح؛ لذا فإن عرض شهادة فورية من دون تقييم يُعد علامة تحذير. تحقّق من الجهة المانحة ونطاق اعتمادها ذي الصلة وشروط قبول المشتري قبل الدفع.",
        },
      },
      {
        q: {
          en: "Is there an ISO certificate for individuals?",
          ar: "هل توجد شهادة ايزو للأفراد؟",
        },
        a: {
          en: "No, ISO management system certificates are issued to organizations, not individuals. What individuals take are training courses, such as internal auditor or lead auditor courses on ISO 9001 or ISO 22000, which end in a course certificate. That is a training credential, not an organizational ISO certificate. When a company asks you for an ISO certificate, it means certification of the organization's management system.",
          ar: "لا توجد شهادة ايزو للأفراد، فشهادات أنظمة الإدارة تُمنح للمنشآت لا للأشخاص. المتاح للأفراد هو الدورات التدريبية، مثل دورات المدقق الداخلي أو المدقق الرئيسي على ايزو 9001 أو ايزو 22000، وتنتهي بشهادة اجتياز الدورة. هذه شهادة تدريب وليست شهادة ايزو للمنشأة. عندما تطلب منك شركة شهادة ايزو، فالمقصود شهادة نظام الإدارة الممنوحة للمنشأة.",
        },
      },
    ],
    services: {
      "iso-9001": [
        {
          q: {
            en: "What is ISO 9001?",
            ar: "ما هي مواصفة ISO 9001؟",
          },
          a: {
            en: "ISO 9001 sets requirements for a quality management system within a defined scope. It concerns how the organisation manages its work, rather than certifying each product or guaranteeing a commercial result.",
            ar: "تحدّد مواصفة ISO 9001 متطلبات نظام إدارة الجودة ضمن نطاق محدّد. وهي تتعلق بطريقة إدارة المؤسسة لأعمالها، ولا تمنح شهادة لكل منتج أو تضمن نتيجة تجارية.",
          },
        },
        {
          q: {
            en: "Who needs ISO 9001 certification?",
            ar: "من يحتاج إلى شهادة ISO 9001؟",
          },
          a: {
            en: "Start with your customer or contract requirement and your internal quality objectives. Where a buyer requests certification, check the exact scope and acceptance conditions. Do not assume the same requirement applies to every Iraqi tender.",
            ar: "ابدأ بمتطلبات العميل أو العقد وأهداف الجودة داخل شركتك. إذا طلب المشتري شهادة، فتحقّق من النطاق المطلوب وشروط القبول. لا تفترض أن المتطلب نفسه ينطبق على جميع المناقصات العراقية.",
          },
        },
        {
          q: {
            en: "What should we prepare for an ISO 9001 quotation?",
            ar: "ماذا نُحضّر لطلب عرض سعر لشهادة ISO 9001؟",
          },
          a: {
            en: "Share your activities, sites, approximate workforce, current system or certificate, the buyer’s wording and target date. Identify whether you need preparation support or already have a system ready for assessment. The written quotation should separate consultancy, certification-body work and exclusions.",
            ar: "اذكر أنشطة الشركة ومواقعها والعدد التقريبي للعاملين والنظام أو الشهادة الحالية ونص متطلب المشتري والموعد المستهدف. وضّح ما إذا كنت تحتاج إلى دعم في التحضير أم أن لديك نظاماً جاهزاً للتقييم. ينبغي أن يفصل عرض السعر المكتوب بين الاستشارات وأعمال جهة منح الشهادات وما لا يشمله العرض.",
          },
        },
        {
          q: {
            en: "How do I get ISO 9001 certified with IraqCert?",
            ar: "كيف أبدأ إجراءات شهادة ISO 9001 مع عراق سيرت؟",
          },
          a: {
            en: "We first clarify your requirement and preparation needs. The agreed work can include a gap review, documentation and implementation support, and coordination of assessment arrangements. The quotation must name the proposed certification body and its accreditation status. That body makes the certification decision.",
            ar: "نبدأ بتوضيح المتطلب واحتياجات التحضير. قد يشمل العمل المتفق عليه مراجعة الفجوات ودعم التوثيق والتطبيق وتنسيق ترتيبات التقييم. يجب أن يسمّي العرض جهة منح الشهادات المقترحة ويوضح حالة اعتمادها. وتتخذ تلك الجهة قرار منح الشهادة.",
          },
        },
        {
          q: {
            en: "What are the requirements of ISO 9001?",
            ar: "ما هي متطلبات ISO 9001؟",
          },
          a: {
            en: "Preparation should address organisational context, leadership, planning, support, operations, performance evaluation and improvement. Practical inputs include defined responsibilities, quality objectives, operating controls and records, internal audit and management review. The scope review establishes what your organisation needs to develop.",
            ar: "ينبغي أن يغطي التحضير سياق المؤسسة والقيادة والتخطيط والدعم والتشغيل وتقييم الأداء والتحسين. وتشمل المدخلات العملية مسؤوليات محدّدة وأهدافاً للجودة وضوابط وسجلات تشغيلية وتدقيقاً داخلياً ومراجعة الإدارة. وتحدّد مراجعة النطاق ما تحتاج مؤسستك إلى تطويره.",
          },
        },
        {
          q: {
            en: "Does ISO 9001 replace product or supplier approval?",
            ar: "هل تحل شهادة ISO 9001 محل الموافقة على المنتج أو تأهيل المورد؟",
          },
          a: {
            en: "No. A management-system certificate does not replace product testing, licences or a buyer’s separate supplier assessment. Confirm what the intended recipient requires before committing to work.",
            ar: "لا. لا تحل شهادة نظام الإدارة محل اختبارات المنتج أو التراخيص أو تقييم المورد الذي يجريه المشتري بصورة مستقلة. تحقّق من متطلبات الجهة المستفيدة قبل الاتفاق على العمل.",
          },
        },
      ],
      "iso-14001": [
        {
          q: { en: "What is ISO 14001?", ar: "ما هو الأيزو 14001؟" },
          a: {
            en: "ISO 14001 is the international standard for an environmental management system. It gives your organization a framework to control its environmental impact and meet environmental obligations in contracts.",
            ar: "الأيزو 14001 هو المعيار الدولي لنظام الإدارة البيئية، ويمنح مؤسستك إطاراً للتحكم في أثرها البيئي والوفاء بالمتطلبات البيئية في العقود.",
          },
        },
        {
          q: { en: "Who needs ISO 14001?", ar: "من يحتاج إلى الأيزو 14001؟" },
          a: {
            en: "Any organization can use ISO 14001 to improve environmental management. If certification is a buyer requirement, confirm the exact standard, scope and acceptance conditions in the tender or supplier criteria.",
            ar: "يمكن لأي مؤسسة استخدام ISO 14001 لتحسين الإدارة البيئية. وإذا اشترط المشتري الحصول على شهادة، فتحقّق من المواصفة والنطاق وشروط القبول الواردة في المناقصة أو معايير الموردين.",
          },
        },
        {
          q: { en: "What are the benefits of ISO 14001?", ar: "ما فوائد الأيزو 14001؟" },
          a: {
            en: "ISO 14001 supports better environmental performance, resource use and management of compliance obligations. Certification may help demonstrate a management system where a buyer requests it, but does not replace permits or guarantee tender qualification or contract award.",
            ar: "يساعد ISO 14001 على تحسين الأداء البيئي واستخدام الموارد وإدارة التزامات الامتثال. وقد تساعد الشهادة في إثبات وجود نظام إدارة عندما يطلبه المشتري، لكنها لا تحل محل التصاريح ولا تضمن التأهل للمناقصة أو إرساء العقد.",
          },
        },
        {
          q: { en: "How do I get ISO 14001 certified?", ar: "كيف أحصل على شهادة الأيزو 14001؟" },
          a: {
            en: "IraqCert benchmarks your current practices against ISO 14001, helps close the gaps, and arranges the accredited audit. Certificates are verifiable on IAF CertSearch.",
            ar: "يقارن عراق سيرت ممارساتك الحالية بمتطلبات الأيزو 14001 ويساعدك على سد الفجوات وينسّق التدقيق المعتمد. والشهادات قابلة للتحقق عبر IAF CertSearch.",
          },
        },
        {
          q: { en: "What are the requirements of ISO 14001?", ar: "ما هي متطلبات ISO 14001؟" },
          a: {
            en: "ISO 14001 requires an environmental management system that identifies how your operations affect the environment, tracks every legal environmental obligation that applies to you, and proves you comply with them. You must also take a lifecycle view of your products and services, set measurable environmental objectives, and prepare for emergencies. Auditors check the system in operation, not just on paper.",
            ar: "تشترط مواصفة ISO 14001 نظاماً للإدارة البيئية يحدد أثر عملياتك في البيئة، ويرصد كل التزام بيئي قانوني ينطبق عليك، ويثبت امتثالك له. كما تتطلب المواصفة نظرة تغطي دورة حياة منتجاتك وخدماتك، وأهدافاً بيئية قابلة للقياس، وجاهزية للطوارئ. ويتحقق المدققون من النظام وهو يعمل، لا على الورق فقط.",
          },
        },
      ],
      "iso-45001": [
        {
          q: { en: "What is ISO 45001?", ar: "ما هو الأيزو 45001؟" },
          a: {
            en: "ISO 45001 is the international standard for an occupational health and safety management system. It replaced OHSAS 18001.",
            ar: "الأيزو 45001 هو المعيار الدولي لنظام إدارة الصحة والسلامة المهنية، وقد حلّ محل معيار OHSAS 18001.",
          },
        },
        {
          q: { en: "Who needs ISO 45001?", ar: "من يحتاج إلى الأيزو 45001؟" },
          a: {
            en: "ISO 45001 can be used by organizations of any size or sector, including higher-risk activities such as construction and oil and gas. Check whether your buyer requires certification and which scope and acceptance conditions apply.",
            ar: "يمكن للمؤسسات بمختلف أحجامها وقطاعاتها استخدام ISO 45001، بما فيها الأنشطة الأعلى خطراً مثل الإنشاءات والنفط والغاز. تحقّق مما إذا كان المشتري يشترط شهادة، ومن النطاق وشروط القبول المنطبقة.",
          },
        },
        {
          q: { en: "What are the benefits of ISO 45001?", ar: "ما فوائد الأيزو 45001؟" },
          a: {
            en: "ISO 45001 helps an organization identify hazards, manage occupational health and safety risks, involve workers and improve safety performance. Certification does not remove legal responsibilities or guarantee tender qualification or a contract award.",
            ar: "يساعد ISO 45001 المؤسسة على تحديد الأخطار وإدارة مخاطر الصحة والسلامة المهنية وإشراك العاملين وتحسين أداء السلامة. ولا تعفي الشهادة من المسؤوليات القانونية أو تضمن التأهل للمناقصة أو إرساء العقد.",
          },
        },
        {
          q: { en: "How do I get ISO 45001 certified?", ar: "كيف أحصل على شهادة الأيزو 45001؟" },
          a: {
            en: "IraqCert assesses your safety systems, helps implement the required controls, and arranges the accredited audit. Certificates are verifiable on IAF CertSearch.",
            ar: "يقيّم عراق سيرت أنظمة السلامة لديك، ويساعد على تطبيق الضوابط المطلوبة، وينسّق التدقيق المعتمد. والشهادات قابلة للتحقق عبر IAF CertSearch.",
          },
        },
        {
          q: { en: "What are the requirements of ISO 45001?", ar: "ما هي متطلبات ISO 45001؟" },
          a: {
            en: "ISO 45001 requires an occupational health and safety management system that identifies workplace hazards, assesses the risks, and puts controls in place before incidents happen. Workers must be consulted and involved, and you must identify and meet every health and safety law that applies to your operations. Auditors verify the system on site, from risk registers to incident records.",
            ar: "تشترط مواصفة ISO 45001 نظاماً لإدارة السلامة والصحة المهنية يحدد مخاطر مكان العمل ويقيّمها ويضع الضوابط قبل وقوع الحوادث. وتوجب المواصفة استشارة العاملين وإشراكهم، ورصد كل قانون للصحة والسلامة ينطبق على عملياتك والامتثال له. ويتحقق المدققون من النظام في الموقع، من سجلات المخاطر إلى سجلات الحوادث.",
          },
        },
      ],
      "iso-22000": [
        {
          q: { en: "What is ISO 22000?", ar: "ما هو الأيزو 22000؟" },
          a: {
            en: "ISO 22000 is the international standard for a food safety management system. It applies across the whole food chain and is built on HACCP principles.",
            ar: "الأيزو 22000 هو المعيار الدولي لنظام إدارة سلامة الغذاء، ويسري على سلسلة الغذاء بأكملها ويقوم على مبادئ الهاسب (HACCP).",
          },
        },
        {
          q: { en: "Who needs ISO 22000?", ar: "من يحتاج إلى الأيزو 22000؟" },
          a: {
            en: "Food producers, processors, packagers, and distributors, especially those exporting to Gulf and international markets that require certified food safety.",
            ar: "منتجو ومصنّعو ومعبّئو وموزّعو الأغذية، خصوصاً المصدّرون إلى أسواق الخليج والأسواق الدولية التي تشترط سلامة غذاء معتمدة.",
          },
        },
        {
          q: { en: "What are the benefits of ISO 22000?", ar: "ما فوائد الأيزو 22000؟" },
          a: {
            en: "It opens export markets, satisfies regulator and buyer requirements, and demonstrates verifiable control of food safety hazards.",
            ar: "يفتح أسواق التصدير، ويلبّي متطلبات الجهات التنظيمية والمشترين، ويثبت تحكماً موثقاً في مخاطر سلامة الغذاء.",
          },
        },
        {
          q: { en: "How do I get ISO 22000 certified?", ar: "كيف أحصل على شهادة الأيزو 22000؟" },
          a: {
            en: "IraqCert maps your processes against ISO 22000 and HACCP, helps close gaps, and arranges the accredited audit. Certificates are verifiable on IAF CertSearch.",
            ar: "يطابق عراق سيرت عملياتك مع متطلبات الأيزو 22000 والهاسب، ويساعد على سد الفجوات، وينسّق التدقيق المعتمد. والشهادات قابلة للتحقق عبر IAF CertSearch.",
          },
        },
        {
          q: { en: "What are the requirements of ISO 22000?", ar: "ما هي متطلبات ISO 22000؟" },
          a: {
            en: "ISO 22000 requires three layers of control: prerequisite programmes that keep hygiene basics in place, a HACCP plan that controls food safety hazards at critical points, and a management system that ties both together across your operation. You must also trace products through your chain and communicate hazards to suppliers and customers. Auditors test all three layers in production, not just the paperwork.",
            ar: "تشترط مواصفة ISO 22000 ثلاث طبقات من الضبط: برامج المتطلبات الأساسية التي تحفظ أساسيات النظافة، وخطة تحليل المخاطر (HACCP) التي تضبط مخاطر سلامة الغذاء عند نقاط التحكم الحرجة، ونظام إدارة يربط الطبقات كلها عبر عملياتك. كما توجب المواصفة تتبع المنتجات عبر سلسلة التوريد وإبلاغ الموردين والعملاء بالمخاطر. ويختبر المدققون الطبقات الثلاث أثناء الإنتاج، لا في الوثائق فقط.",
          },
        },
        {
          q: {
            en: "Is HACCP enough or do I need ISO 22000?",
            ar: "هل يكفي نظام HACCP أم أحتاج إلى شهادة ايزو 22000؟",
          },
          a: {
            en: "HACCP alone is rarely enough when a buyer or export market asks for a certified system. ISO 22000 contains the HACCP principles and adds a full food safety management system covering management responsibility, communication, and continual improvement. Retail chains, oil and gas clients, and export markets usually ask for the certified system, which means ISO 22000. Certifying to ISO 22000 covers the HACCP requirement and more.",
            ar: "نظام HACCP وحده لا يكفي عادة عندما يطلب المشتري أو سوق التصدير نظاماً حاصلاً على شهادة معتمدة. مواصفة ايزو 22000 تتضمن مبادئ HACCP وتضيف إليها نظاماً متكاملاً لإدارة سلامة الغذاء يشمل مسؤولية الإدارة والتواصل والتحسين المستمر. سلاسل التجزئة وشركات النفط والغاز وأسواق التصدير تطلب في الغالب النظام المعتمد بشهادة، أي ايزو 22000. شهادة ايزو 22000 تغطي متطلبات HACCP وتزيد عليها.",
          },
        },
        {
          q: {
            en: "What is the difference between HACCP and ISO 22000?",
            ar: "ما الفرق بين HACCP وايزو 22000؟",
          },
          a: {
            en: "HACCP is a method for identifying and controlling food safety hazards, while ISO 22000 is an international standard that builds those principles into a certifiable food safety management system. ISO 22000 adds prerequisite programs, management commitment, and continual improvement. You can apply HACCP internally without a certificate. Only ISO 22000 gives you an accredited certificate that a buyer can verify.",
            ar: "نظام HACCP منهجية لتحديد مخاطر سلامة الغذاء والسيطرة عليها، بينما ايزو 22000 مواصفة دولية تدمج مبادئ HACCP ضمن نظام إدارة لسلامة الغذاء تُمنح عليه شهادة معتمدة. تضيف ايزو 22000 برامج المتطلبات الأساسية والتزام الإدارة والتحسين المستمر. يمكنك تطبيق HACCP داخلياً من دون شهادة. أما ايزو 22000 فتمنحك شهادة معتمدة يستطيع المشتري التحقق منها.",
          },
        },
      ],
      "iso-29001": [
        {
          q: { en: "What is ISO 29001?", ar: "ما هو الأيزو 29001؟" },
          a: {
            en: "ISO 29001 is the quality management standard for the petroleum, petrochemical, and natural gas industry. It builds on ISO 9001 and adds sector-specific controls for defect prevention and supply-chain risk.",
            ar: "الأيزو 29001 هو معيار إدارة الجودة لقطاع النفط والبتروكيماويات والغاز الطبيعي، ويبني على الأيزو 9001 ويضيف ضوابط خاصة بالقطاع لمنع العيوب وإدارة مخاطر سلسلة التوريد.",
          },
        },
        {
          q: { en: "Who needs ISO 29001?", ar: "من يحتاج إلى الأيزو 29001؟" },
          a: {
            en: "Product and service suppliers whose activities fall within the standard’s scope may use it. Whether certification is required depends on the buyer and contract; confirm the exact requirement before proceeding.",
            ar: "يمكن أن يستخدمها موردو المنتجات والخدمات الذين تدخل أنشطتهم ضمن نطاق المواصفة. ويتوقف اشتراط الشهادة على المشتري والعقد؛ فأكّد المتطلب بدقة قبل البدء.",
          },
        },
        {
          q: { en: "Is ISO 29001 the same as API Q1?", ar: "هل الأيزو 29001 نفسه معيار API Q1؟" },
          a: {
            en: "No. ISO 29001 and API Q1 are distinct documents and are not automatically interchangeable. API Q1’s tenth edition includes product providers and specified non-manufacturing activities; API Q2 addresses service supply organizations. Confirm the activity, edition and evidence your buyer requires.",
            ar: "لا. ISO 29001 وAPI Q1 وثيقتان مختلفتان وليستا بديلين متكافئين تلقائياً. يشمل الإصدار العاشر من API Q1 مقدمي المنتجات وأنشطة غير تصنيعية محددة، ويختص API Q2 بمؤسسات تقديم الخدمات. أكّد النشاط والإصدار والأدلة التي يشترطها المشتري.",
          },
        },
        {
          q: { en: "How do I get ISO 29001 certified?", ar: "كيف أحصل على شهادة الأيزو 29001؟" },
          a: {
            en: "IraqCert prepares your quality system to ISO 29001 requirements and coordinates the certification audit with an accredited certification body. ISO 29001 is outside the UAF-accredited scope of our partner body AQSR (CM-MS-7820), so we confirm the issuing body and its accreditation scope with you before the audit is booked.",
            ar: "يهيّئ عراق سيرت نظام الجودة لديك وفق متطلبات ISO 29001 وينسّق تدقيق الاعتماد مع جهة مانحة معتمدة. ولأن ISO 29001 خارج نطاق اعتماد UAF لشريكنا AQSR (رقم CM-MS-7820)، نؤكد لك الجهة المانحة ونطاق اعتمادها قبل حجز التدقيق.",
          },
        },
        {
          q: { en: "What are the requirements of ISO 29001?", ar: "ما هي متطلبات ISO 29001؟" },
          a: {
            en: "ISO 29001:2020 supplements ISO 9001:2015 with sector requirements and guidance on supply-chain risks and opportunities. Review the applicable standard and amendment with the proposed certification body. An existing ISO 9001 certificate does not establish ISO 29001 conformity.",
            ar: "تكمل ISO 29001:2020 مواصفة ISO 9001:2015 بمتطلبات وإرشادات قطاعية بشأن مخاطر سلسلة التوريد وفرصها. راجع المواصفة والتعديل المنطبقين مع الجهة المانحة المقترحة. ولا تثبت شهادة ISO 9001 القائمة المطابقة لمواصفة ISO 29001.",
          },
        },
      ],
      "iso-27001": [
        {
          q: { en: "What is ISO 27001?", ar: "ما هو ISO 27001؟" },
          a: {
            en: "ISO 27001 is the international standard for information security management systems (ISMS). It defines how an organization identifies its information risks, treats them through a set of controls, and proves to an independent auditor that the system works. The current edition, ISO/IEC 27001:2022, lists 93 controls in Annex A, covering organizational, people, physical, and technological security. Certification means an accredited body has audited your ISMS and confirmed it meets the standard.",
            ar: "ISO 27001 هو المعيار الدولي لأنظمة إدارة أمن المعلومات (ISMS). يحدد المعيار كيف تكتشف المنظمة مخاطر معلوماتها وتعالجها عبر مجموعة من الضوابط، ثم تثبت لمدقق مستقل أن النظام يعمل. وتضم النسخة الحالية من المعيار، ISO/IEC 27001:2022، ما مجموعه 93 ضابطاً في الملحق A تغطي الجوانب التنظيمية والبشرية والمادية والتقنية. الحصول على الشهادة يعني أن جهة إصدار معتمدة دققت نظامكم وأكدت مطابقته للمعيار.",
          },
        },
        {
          q: { en: "Who needs ISO 27001 in Iraq?", ar: "من يحتاج إلى شهادة ISO 27001 في العراق؟" },
          a: {
            en: "A buyer, contract or applicable requirement may call for ISO 27001 or other security evidence. Start with your information risks and the exact requirement. Do not assume every bank, telecom supplier or company handling customer data must be certified.",
            ar: "قد يطلب المشتري أو العقد أو المتطلب المنطبق ISO 27001 أو أدلة أمن أخرى. ابدأ بمخاطر معلوماتك والمتطلب المحدد. لا تفترض أن الشهادة إلزامية لكل مصرف أو مورد اتصالات أو شركة تتعامل مع بيانات العملاء.",
          },
        },
        {
          q: { en: "What are the requirements of ISO 27001?", ar: "ما هي متطلبات ISO 27001؟" },
          a: {
            en: "ISO 27001 requires a documented information security management system built on risk assessment. You define the scope of the system, assess your information risks, select the controls that apply from the 93 in Annex A and justify them in a Statement of Applicability, implement them, train staff, run internal audits, and review the system at management level. An accredited certification body then audits the system in two stages before issuing the certificate.",
            ar: "يتطلب ISO 27001 نظاماً موثقاً لإدارة أمن المعلومات يقوم على تقييم المخاطر. عليكم تحديد نطاق النظام، وتقييم مخاطر المعلومات، واختيار الضوابط المنطبقة من بين 93 ضابطاً في الملحق A مع تبرير الاختيار في بيان قابلية التطبيق، ثم تنفيذها وتدريب الموظفين وإجراء تدقيقات داخلية ومراجعة النظام على مستوى الإدارة. بعد ذلك تدقق جهة إصدار معتمدة النظام على مرحلتين قبل منح الشهادة.",
          },
        },
        {
          q: { en: "What are the benefits of ISO 27001?", ar: "ما هي فوائد شهادة ISO 27001؟" },
          a: {
            en: "ISO 27001 provides a framework for managing information-security risks and improving the management system. A relevant certificate may support a buyer’s assessment, but does not guarantee tender qualification, contract award, secure data or exemption from security questionnaires.",
            ar: "يوفر ISO 27001 إطاراً لإدارة مخاطر أمن المعلومات وتحسين نظام الإدارة. قد تدعم الشهادة ذات الصلة تقييم المشتري، لكنها لا تضمن التأهيل للمناقصة أو إرساء عقد أو أمن البيانات أو الإعفاء من الاستبيانات الأمنية.",
          },
        },
        {
          q: {
            en: "How do I get ISO 27001 certified in Iraq?",
            ar: "كيف أحصل على شهادة ISO 27001 في العراق؟",
          },
          a: {
            en: "Prepare and operate your ISMS, then arrange assessment with the proposed certification body. IraqCert supports the agreed preparation and coordination work; that body makes the certification decision. Confirm its accreditation scope, certificate verification route, ongoing audit arrangements and buyer acceptance before booking.",
            ar: "جهّز نظام إدارة أمن المعلومات وطبّقه، ثم رتّب التقييم مع جهة منح الشهادات المقترحة. تدعم عراق سيرت أعمال التحضير والتنسيق المتفق عليها، وتتخذ جهة المنح قرار الشهادة. تحقّق من نطاق اعتمادها ومسار التحقق من الشهادة وترتيبات التدقيق المستمر وقبول المشتري قبل الحجز.",
          },
        },
      ],
      "ministry-of-oil-vendor-registration": [
        {
          q: {
            en: "How do I prepare for Iraqi Ministry of Oil vendor registration?",
            ar: "كيف أستعد لتسجيل الموردين لدى وزارة النفط العراقية؟",
          },
          a: {
            en: "Identify the exact Ministry or state oil company request, your activity and the current procurement contact or instructions. IraqCert reviews the Iraqi company dossier against that request and helps identify document gaps. There is no single registration that covers every company, field and tender.",
            ar: "حدّد طلب الوزارة أو شركة النفط الحكومية المعنية ونشاطك وجهة الاتصال الحالية بالمشتريات أو تعليماتها. تراجع عراق سيرت ملف الشركة العراقية وفق ذلك الطلب وتساعد في تحديد نواقص الوثائق. لا يوجد تسجيل واحد يشمل جميع الشركات والحقول والمناقصات.",
          },
        },
        {
          q: { en: "Which documents should I prepare?", ar: "ما الوثائق التي ينبغي إعدادها؟" },
          a: {
            en: "Use the receiving entity's current list. Depending on the activity and request, the review may cover the company profile, legal records, financial capacity, relevant experience, technical resources, HSE evidence, insurance and specified certificates. Do not treat this as a universal document checklist.",
            ar: "اعتمد القائمة الحالية للجهة المستلمة. بحسب النشاط والطلب، قد تشمل المراجعة الملف التعريفي للشركة والسجلات القانونية والقدرة المالية والخبرة ذات الصلة والموارد الفنية ووثائق الصحة والسلامة والبيئة والتأمين والشهادات المحددة. هذه ليست قائمة وثائق موحدة لجميع الحالات.",
          },
        },
        {
          q: {
            en: "Is ISO certification compulsory for every vendor?",
            ar: "هل شهادة الأيزو إلزامية لكل مورد؟",
          },
          a: {
            en: "No universal ISO requirement can be assumed. Check the exact buyer, activity, category and tender criteria. A requested certificate must match the required scope; certification alone does not establish vendor eligibility or guarantee approval.",
            ar: "لا يصح افتراض وجود شرط أيزو موحد. تحقّق من معايير الجهة المشترية والنشاط والفئة والمناقصة المعنية. يجب أن تطابق الشهادة المطلوبة النطاق المحدد؛ ولا تثبت الشهادة وحدها أهلية المورد أو تضمن الموافقة.",
          },
        },
        {
          q: { en: "How long does registration take?", ar: "كم يستغرق التسجيل؟" },
          a: {
            en: "Timing depends on the receiving entity, the route and the completeness of requested evidence. IraqCert can scope a dossier review after seeing the request, but cannot promise the buyer's decision date or approval.",
            ar: "تعتمد المدة على الجهة المستلمة والمسار واكتمال الأدلة المطلوبة. يمكن لعراق سيرت تحديد نطاق مراجعة الملف بعد الاطلاع على الطلب، لكنها لا تستطيع ضمان موعد قرار الجهة المشترية أو موافقتها.",
          },
        },
      ],
      "oil-gas-registration": [
        {
          q: {
            en: "Which oil and gas registration route should my Iraqi company use?",
            ar: "ما مسار تسجيل موردي النفط والغاز المناسب لشركتي العراقية؟",
          },
          a: {
            en: "Start with the buyer or project you want to supply and your actual products or services. BOC, Rumaila, BGC and MNR have different requirements. Use the relevant guide and current official instructions, then ask IraqCert to review your dossier for that specific route.",
            ar: "ابدأ بالجهة المشترية أو المشروع الذي تريد التوريد له وبمنتجاتك أو خدماتك الفعلية. تختلف متطلبات شركة نفط البصرة والرميلة وشركة غاز البصرة ووزارة الثروات الطبيعية. راجع الدليل المعني والتعليمات الرسمية الحالية، ثم اطلب من عراق سيرت مراجعة ملفك لذلك المسار المحدد.",
          },
        },
        {
          q: {
            en: "Does company registration mean I am on an approved vendor list?",
            ar: "هل يعني تسجيل الشركة إدراجها في قائمة الموردين المعتمدين؟",
          },
          a: {
            en: "Not necessarily. Company or portal registration, activity-specific vendor approval and tender qualification can be separate steps. MNR company registration and category-specific AVL listing are also distinct. Check the route's eligibility conditions; registration does not guarantee a listing, invitation or award.",
            ar: "ليس بالضرورة. قد يكون تسجيل الشركة أو الحساب على البوابة واعتماد المورد لنشاط محدد والتأهيل للمناقصة مراحل منفصلة. كما يختلف تسجيل الشركة لدى وزارة الثروات الطبيعية عن إدراجها في قائمة الموردين المعتمدين لفئة محددة. تحقّق من شروط أهلية المسار؛ فالتسجيل لا يضمن الإدراج أو الدعوة أو إرساء عقد.",
          },
        },
        {
          q: {
            en: "What should I send for a dossier review?",
            ar: "ماذا أرسل لطلب مراجعة الملف؟",
          },
          a: {
            en: "Start with your company name, country of incorporation, activity, target buyer or project, target date and business contact. Share the current buyer request or document list if available. We can then define which company-profile, legal, financial, technical and HSE evidence needs review.",
            ar: "ابدأ باسم الشركة وبلد تأسيسها والنشاط والجهة المشترية أو المشروع المستهدف والموعد المستهدف ووسيلة تواصل العمل. أرسل طلب الجهة المشترية الحالي أو قائمة وثائقها إن توفرت. يمكننا بعد ذلك تحديد ما يحتاج إلى مراجعة من الملف التعريفي للشركة والأدلة القانونية والمالية والفنية ووثائق الصحة والسلامة والبيئة.",
          },
        },
        {
          q: {
            en: "Do all oil and gas vendors need ISO certification?",
            ar: "هل يحتاج جميع موردي النفط والغاز إلى شهادة أيزو؟",
          },
          a: {
            en: "No universal requirement applies across all routes. The buyer may request a particular ISO standard or other evidence for the activity or tender. Check those criteria before starting certification; a certificate alone does not guarantee qualification or a contract.",
            ar: "لا يوجد شرط موحد ينطبق على جميع المسارات. قد تطلب الجهة المشترية معيار أيزو معيناً أو أدلة أخرى للنشاط أو المناقصة. تحقّق من تلك المعايير قبل بدء إجراءات الشهادة؛ فالشهادة وحدها لا تضمن التأهيل أو الحصول على عقد.",
          },
        },
      ],
    },
  },
  footer: {
    tagline: {
      en: "Management-system preparation and supplier registration support.",
      ar: "دعم التحضير لأنظمة الإدارة وتسجيل الموردين.",
    },
    accreditation: {
      label: { en: "Company-reported partner", ar: "الشريك وفق إفادة الشركة" },
      body: {
        en: "According to company-reported information, AQSR is our certification partner for the five standards listed on our About page. UAF accreditation No. CM-MS-7820 belongs to AQSR, not IraqCert. ISO 29001 is outside AQSR's disclosed UAF-accredited scope. IraqCert provides preparation support and confirms the issuing body and its accreditation scope before booking an ISO 29001 audit.",
        ar: "وفقاً لإفادة الشركة، AQSR هي جهة المنح الشريكة للمعايير الخمسة المذكورة في صفحة «من نحن». رقم اعتماد UAF هو CM-MS-7820 ويخص AQSR، وليس عراق سيرت. معيار ISO 29001 خارج نطاق اعتماد UAF المعلن لشركة AQSR. تقدم عراق سيرت دعم التحضير، وتؤكد الجهة المانحة ونطاق اعتمادها قبل حجز تدقيق ISO 29001.",
      },
      verify: { en: "Verify on IAF CertSearch", ar: "تحقّق عبر IAF CertSearch" },
      verifyUrl: "https://www.iafcertsearch.org/",
    },
    quickLinks: { en: "Quick Links", ar: "روابط الوصول السريع" },
    contactTitle: { en: "Contact Us", ar: "تواصل معنا" },
    socialTitle: { en: "Follow Us", ar: "تابعنا" },
    emailLabel: { en: "Email", ar: "البريد الإلكتروني" },
    email: { en: "info@iraqcert.com", ar: "info@iraqcert.com" },
    offices: [
      {
        id: "erbil",
        city: { en: "Based in Erbil", ar: "مقرّنا في أربيل" },
        address: { en: "Serving companies across Iraq", ar: "نخدم الشركات في أنحاء العراق" },
        phone: "(+964) 787 494 6122",
        wa: "https://wa.me/9647874946122",
      },
    ],
    rights: {
      en: "All rights reserved.",
      ar: "جميع الحقوق محفوظة لشركة عراق سيرت.",
    },
  },
} as const;

export const privacyPolicy = {
  header: {
    title: { en: "Privacy Policy", ar: "سياسة الخصوصية" },
    lastUpdated: { en: "Last Updated: June 2026", ar: "آخر تحديث: حزيران (يونيو) 2026" },
    intro: {
      en: "As a premier ISO certification and compliance auditing firm in Iraq, IraqCert recognizes that absolute confidentiality is the cornerstone of our industry. This Privacy Policy outlines our strict protocols for collecting, safeguarding, and utilizing your corporate and personal data.",
      ar: "بصفتنا جهة رائدة في مجال التدقيق واعتمادات الآيزو في العراق، تدرك شركة عراق سيرت أن السرية المهنية التامة هي الركيزة الأساسية لعملنا. توضح سياسة الخصوصية هذه بروتوكولاتنا الصارمة لجمع وحماية واستخدام بياناتك الشخصية والمؤسسية.",
    },
  },
  sections: [
    {
      id: "data-collection",
      title: { en: "1. Information We Collect", ar: "1. البيانات التي نقوم بجمعها" },
      content: {
        en: "To execute our rigorous certification and compliance services, we collect professional contact details (names, corporate emails, phone numbers), business registration documentation, operational procedures, and any proprietary data strictly required for ISO gap analysis, audits, and Approved Vendor List (AVL) registrations.",
        ar: "لتنفيذ خدماتنا الدقيقة في التدقيق والاعتماد، نقوم بجمع معلومات التواصل المهنية (الأسماء، رسائل البريد الإلكتروني للعمل، أرقام الهواتف)، وثائق التسجيل التجاري، الإجراءات التشغيلية، وأي بيانات خاصة تتطلبها حصراً عمليات تحليل فجوات الآيزو، التدقيق، والتسجيل في قوائم الموردين المعتمدين (AVL).",
      },
    },
    {
      id: "data-usage",
      title: { en: "2. How We Use Your Information", ar: "2. الغرض من استخدام البيانات" },
      content: {
        en: "Your data is utilized exclusively for professional purposes: facilitating ISO certification audits, submitting pre-qualification documents to Iraqi state-owned entities and International Oil Companies (IOCs), managing your account, and fulfilling our legal and regulatory compliance obligations.",
        ar: "تُستخدم بياناتك حصرياً للأغراض المهنية وتشمل: تسهيل عمليات تدقيق الآيزو، تقديم وثائق التأهيل المسبق للشركات الحكومية العراقية وشركات النفط العالمية (IOCs)، إدارة حسابك، والوفاء بالتزاماتنا القانونية والتنظيمية.",
      },
    },
    {
      id: "data-sharing",
      title: { en: "3. Information Sharing & Disclosure", ar: "3. مشاركة البيانات والإفصاح عنها" },
      content: {
        en: "IraqCert operates under strict Non-Disclosure Agreements (NDAs). We do not sell or rent your data. Information is only shared with accredited international certification bodies, official Iraqi regulatory authorities, or IOC procurement portals when explicitly required to secure your requested certifications or vendor approvals.",
        ar: "تعمل شركة عراق سيرت بموجب اتفاقيات صارمة لعدم الإفصاح (NDAs). نحن لا نبيع أو نؤجر بياناتك لأي جهة. تتم مشاركة المعلومات فقط مع هيئات الاعتماد الدولية، السلطات التنظيمية العراقية الرسمية، أو بوابات مشتريات شركات النفط العالمية عندما يكون ذلك مطلوباً بشكل صريح لتأمين شهاداتك أو اعتماداتك المطلوبة.",
      },
    },
    {
      id: "data-security",
      title: { en: "4. Data Security Protocols", ar: "4. بروتوكولات أمن وحماية البيانات" },
      content: {
        en: "We deploy industry-standard encryption, secure localized servers, and stringent access controls to prevent unauthorized access, alteration, or disclosure of your sensitive corporate information. Only authorized auditors assigned to your specific file are granted access to your operational data.",
        ar: "نعتمد أحدث معايير التشفير الصناعية، خوادم آمنة، وضوابط وصول صارمة لمنع أي وصول غير مصرح به أو تعديل أو إفشاء لمعلومات مؤسستك الحساسة. يُمنح حق الوصول إلى بياناتك التشغيلية فقط للمدققين المعتمدين والمكلفين بملفك الخاص.",
      },
    },
    {
      id: "data-retention",
      title: { en: "5. Data Retention", ar: "5. الاحتفاظ بالبيانات" },
      content: {
        en: "Audit reports, compliance documentation, and certification records are securely retained only for the duration mandated by international ISO accreditation standards and Iraqi federal law, after which they are permanently and securely destroyed.",
        ar: "يتم الاحتفاظ بتقارير التدقيق، وثائق الامتثال، وسجلات الاعتماد بشكل آمن فقط للمدة التي تفرضها معايير اعتماد الآيزو الدولية والقانون الاتحادي العراقي، ليتم بعد ذلك إتلافها بشكل دائم وآمن.",
      },
    },
    {
      id: "user-rights",
      title: { en: "6. Your Corporate Rights", ar: "6. حقوق مؤسستك" },
      content: {
        en: "As our client, you retain the right to request access to the data we hold, demand corrections to inaccurate records, or request the deletion of your data, provided such deletion does not violate the legal retention policies set forth by global accreditation bodies.",
        ar: "بصفتك عميلاً لدينا، تحتفظ بحق طلب الوصول إلى البيانات التي نحتفظ بها، أو طلب تصحيح السجلات غير الدقيقة، أو طلب حذف بياناتك، شريطة ألا يتعارض هذا الحذف مع سياسات الاحتفاظ القانونية التي تحددها هيئات الاعتماد العالمية.",
      },
    },
  ],
  contact: {
    title: { en: "Contacting Our Data Protection Team", ar: "التواصل مع فريق حماية البيانات" },
    content: {
      en: "For inquiries regarding our privacy practices or to exercise your data rights, please contact our HQ at info@iraqcert.com.",
      ar: "للاستفسارات المتعلقة بممارسات الخصوصية لدينا أو لممارسة حقوق بياناتك، يرجى التواصل مع مقرنا الرئيسي عبر البريد الإلكتروني: info@iraqcert.com.",
    },
  },
} as const;

export const phones = ["(+964) 787 494 6122", "(+964) 750 494 6122", "(+358) 41 317 9991"];

export const whatsappUrl = "https://wa.me/9647874946122";
